<template>
  <div
    ref="billingPage"
    class="pricing-page clipr-theme twitch-theme"
    :class="{ 'pricing-page--embedded': embedded }"
  >
    <div class="pricing-inner">

      <section v-if="cancellationResult" class="pricing-section pricing-cancellation-card" role="status" aria-live="polite">
        <div class="pricing-cancellation-card-inner">
          <div class="pricing-cancellation-header">
            <span class="pricing-cancellation-icon" aria-hidden="true">
              <i class="ti-check"></i>
            </span>
            <div>
              <p class="pricing-cancellation-title mb-0">
                {{ tt("billing.subscriptionCancelledTitle", "Suscripción cancelada") }}
              </p>
              <p class="pricing-cancellation-summary mb-0">
                {{ tt("billing.subscriptionCancelledSummary", "Sin más cargos.") }}
              </p>
            </div>
          </div>
          <p v-if="cancellationAccessUntil" class="pricing-cancellation-date mb-0">
            {{ tt("billing.subscriptionCancelledAccessUntil", "Acceso hasta {date}")
              .replace("{date}", formatLocalDate(cancellationAccessUntil)) }}
          </p>
          <div v-if="tokenBreakdown.hasData" class="pricing-cancellation-stats">
            <div class="pricing-cancellation-stat">
              <span class="pricing-cancellation-stat-label">
                {{ tt("billing.subscriptionCancelledPlanTokens", "Plan") }}
              </span>
              <span class="pricing-cancellation-stat-value">
                {{ tokenBreakdown.expiring.toLocaleString() }}
              </span>
            </div>
            <div class="pricing-cancellation-stat">
              <span class="pricing-cancellation-stat-label">
                {{ tt("billing.subscriptionCancelledPurchasedTokens", "Compra") }}
              </span>
              <span class="pricing-cancellation-stat-value">
                {{ tokenBreakdown.payg.toLocaleString() }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section v-if="showSubscriptionIssueBanner" class="pricing-section pricing-subscription-alert" role="alert" aria-live="polite">
        <div class="pricing-subscription-alert-copy">
          <p class="pricing-subscription-alert-title mb-1">{{ tt("billing.renewalPaymentIssueTitle", "Hubo un problema con la renovación") }}</p>
          <p class="pricing-subscription-alert-text mb-0">{{ subscriptionIssueBannerText }}</p>
        </div>
        <button
          v-if="canUpdatePaymentMethod"
          type="button"
          class="pricing-subscription-alert-action"
          :disabled="loading"
          @click="openPaymentMethodUpdate"
        >
          {{ tt("billing.updatePaymentMethod", "Actualizar método de pago") }}
        </button>
      </section>

      <div class="pricing-flow-shell" :class="{ 'pricing-flow-shell--checkout': checkoutMode }">
        <div class="pricing-flow-track">
          <div class="pricing-flow-pane pricing-flow-pane--catalog">
            <header class="pricing-catalog-hero">
              <h1 class="pricing-catalog-hero__title">{{ tt("billing.plansTitle", "Elige tu plan") }}</h1>
              <p class="pricing-catalog-hero__subtitle">{{ tt("billing.plansDescription", "Empieza gratis o desbloquea todo con Unlimited.") }}</p>
            </header>

            <div class="billing-cycle-switch-wrapper">
              <div class="billing-cycle-switch" role="group" :aria-label="tt('billing.billingCycle', 'Ciclo de facturación')">
                <button
                  type="button"
                  class="billing-cycle-option"
                  :class="{ 'billing-cycle-option-active': billingCycle === 'monthly' }"
                  :aria-pressed="billingCycle === 'monthly' ? 'true' : 'false'"
                  :aria-label="tt('billing.monthlyPlans', 'Planes mensuales')"
                  @click="setBillingCycle('monthly')"
                >
                  {{ tt("billing.monthly", "Mensual") }}
                </button>
                <button
                  type="button"
                  class="billing-cycle-option"
                  :class="{ 'billing-cycle-option-active': billingCycle === 'annual' }"
                  :aria-pressed="billingCycle === 'annual' ? 'true' : 'false'"
                  :aria-label="tt('billing.annualPlans', 'Planes anuales')"
                  @click="setBillingCycle('annual')"
                >
                  {{ tt("billing.annual", "Anual") }}
                </button>
              </div>
            </div>

            <div class="pricing-section-stack">
              <section class="pricing-section--catalog">
                <div v-if="canUpdatePaymentMethod" class="pricing-plan-actions">
                  <span>{{ tt("billing.manageSubscription", "Gestiona tu suscripción") }}</span>
                  <div class="pricing-plan-actions__buttons">
                    <button
                      type="button"
                      class="pricing-plan-action-btn pricing-plan-action-btn--primary"
                      :disabled="loading"
                      @click="openPaymentMethodUpdate"
                    >
                      <i class="ti-credit-card"></i>
                      {{ tt("billing.updatePaymentMethod", "Actualizar método de pago") }}
                    </button>
                  </div>
                </div>

                <div class="plans-grid">
                  <article
                    class="plan-card plan-card--free"
                    :class="{ 'plan-card-current': isFreePlanCurrent }"
                  >
                    <div class="plan-card-glow" aria-hidden="true"></div>
                    <div class="plan-card-inner">
                      <div class="plan-card-main">
                        <h3 class="plan-name">{{ tt("billing.freePlanName", "Free") }}</h3>
                        <p class="plan-token-line">
                          <i class="ti-package" aria-hidden="true"></i>
                          {{ tt("billing.freeFeatureTokens", "240 tokens al mes") }}
                        </p>
                      </div>

                      <div class="plan-card-buy">
                        <div class="plan-price-row">
                          <div class="plan-price-mainline">
                            <span class="plan-price">
                              $0.00
                              <span class="plan-price-suffix"
                                >USD<span class="plan-billing">/{{ tt("billing.month", "mes") }}</span></span
                              >
                            </span>
                          </div>
                        </div>
                        <p class="plan-price-caption">{{ tt("billing.freePlanCaption", "Sin costo, para siempre") }}</p>
                        <p class="plan-token-equivalency mb-0">
                          {{ tt("billing.tokenEquivalencyHint", "1 token = 1 minute") }}
                        </p>
                      </div>

                      <ul class="plan-highlights-list">
                        <li
                          v-for="feature in freePlanIncludedFeatures"
                          :key="`free-included-${feature}`"
                          class="plan-highlights-item"
                        >
                          <span class="plan-benefit-check" aria-hidden="true">
                            <i class="ti-check"></i>
                          </span>
                          <span>{{ feature }}</span>
                        </li>
                        <li
                          v-for="feature in freePlanExcludedFeatures"
                          :key="`free-excluded-${feature}`"
                          class="plan-highlights-item plan-highlights-item--excluded"
                        >
                          <span class="plan-benefit-check plan-benefit-check--excluded" aria-hidden="true">
                            <i class="ti-close"></i>
                          </span>
                          <span>{{ feature }}</span>
                        </li>
                      </ul>
                    </div>
                  </article>

                  <article
                    v-if="unlimitedOffer"
                    :key="unlimitedOffer.code"
                    class="plan-card plan-card-featured"
                    :class="{
                      'plan-card-active': selectedOfferCode === unlimitedOffer.code,
                      'plan-card-clickable': !isPlanSelectionDisabled(unlimitedOffer),
                      'plan-card-current': isCurrentSubscriptionPlan(unlimitedOffer),
                    }"
                    @click="isPlanSelectionDisabled(unlimitedOffer) ? null : choosePlan(unlimitedOffer)"
                  >
                    <div class="plan-card-glow" aria-hidden="true"></div>
                    <span class="plan-card-ribbon plan-card-ribbon--featured">
                      <i :class="planVolumeDiscountPercent(unlimitedOffer) ? 'ti-bolt' : 'ti-crown'" aria-hidden="true"></i>
                      {{
                        planVolumeDiscountPercent(unlimitedOffer)
                          ? tt("billing.launchOfferBadge", "Oferta de lanzamiento")
                          : tt("billing.unlimitedPlanBadge", "Todo incluido")
                      }}
                    </span>
                    <div class="plan-card-inner">
                      <div class="plan-card-main">
                        <h3 class="plan-name">{{ tt("billing.unlimitedPlanName", "Unlimited") }}</h3>
                        <p class="plan-token-line">
                          <i class="ti-package" aria-hidden="true"></i>
                          {{ tt("billing.unlimitedFeatureTokens", "720 tokens al día") }}
                        </p>
                      </div>

                      <div class="plan-card-buy">
                        <div class="plan-price-row">
                          <div class="plan-price-mainline">
                            <span
                              v-if="billingCycle === 'annual' || planVolumeDiscountPercent(unlimitedOffer)"
                              class="plan-price-original"
                              :aria-label="`${tt('billing.originalPriceLabel', 'Precio original')}: $${formatPrice(billingCycle === 'annual' ? unlimitedMonthlyOffer.price : planOriginalPrice(unlimitedOffer))} USD`"
                            >
                              ${{ formatPrice(billingCycle === 'annual' ? unlimitedMonthlyOffer.price : planOriginalPrice(unlimitedOffer)) }}
                            </span>
                            <span
                              class="plan-price"
                              :class="{ 'plan-price--discounted': billingCycle === 'annual' || !!planVolumeDiscountPercent(unlimitedOffer) }"
                              :aria-label="`${tt('billing.offerPriceLabel', 'Precio de oferta')}: $${formatPrice(billingCycle === 'annual' ? annualMonthlyEquivalent : unlimitedOffer.price)} USD/${billingCycle === 'annual' ? tt('billing.month', 'mes') : planBillingPeriodLabel(unlimitedOffer)}`"
                            >
                              ${{ formatPrice(billingCycle === 'annual' ? annualMonthlyEquivalent : unlimitedOffer.price) }}
                              <span class="plan-price-suffix"
                                >USD<span class="plan-billing">/{{ billingCycle === 'annual' ? tt("billing.month", "mes") : planBillingPeriodLabel(unlimitedOffer) }}</span></span
                              >
                            </span>
                          </div>
                        </div>
                        <div
                          v-if="planVolumeDiscountPercent(unlimitedOffer) || (billingCycle === 'annual' && annualSavingsLabel)"
                          class="plan-discount-row plan-discount-row--stack"
                        >
                          <span
                            v-if="billingCycle !== 'annual' && planVolumeDiscountPercent(unlimitedOffer)"
                            class="plan-price-discount-pill plan-price-discount-pill--launch"
                          >
                            <i class="ti-bolt" aria-hidden="true"></i>
                            <span class="plan-price-discount-value">
                              {{ tt("billing.launchOfferPill", "{percent}% OFF").replace("{percent}", String(planVolumeDiscountPercent(unlimitedOffer))) }}
                            </span>
                          </span>
                          <span v-if="billingCycle === 'annual' && annualSavingsLabel" class="plan-price-discount-pill">
                            <span class="plan-price-discount-word">{{ annualSavingsLabel }}</span>
                          </span>
                        </div>
                        <p class="plan-token-equivalency mb-0">
                          {{ tt("billing.tokenEquivalencyHint", "1 token = 1 minute") }}
                        </p>
                      </div>

                      <ul class="plan-highlights-list">
                        <li
                          v-for="feature in unlimitedPlanFeatures"
                          :key="`unlimited-${feature.label}`"
                          class="plan-highlights-item"
                        >
                          <span class="plan-benefit-check" aria-hidden="true">
                            <i class="ti-check"></i>
                          </span>
                          <span>{{ feature.label }}</span>
                          <span v-if="feature.comingSoon" class="plan-feature-coming-soon">
                            {{ tt("billing.comingSoon", "Próximamente") }}
                          </span>
                        </li>
                      </ul>

                      <div class="plan-card-actions">
                        <button
                          type="button"
                          class="btn btn-primary btn-round plan-card-cta"
                          :class="{ 'plan-card-cta--current': isCurrentSubscriptionPlan(unlimitedOffer) }"
                          :disabled="loading"
                          @click.stop="isCurrentSubscriptionPlan(unlimitedOffer) ? openSubscriptionManagement() : choosePlan(unlimitedOffer)"
                        >
                          {{ planCtaLabel(unlimitedOffer) }}
                        </button>
                      </div>
                    </div>
                  </article>
                </div>
              </section>
            </div>
          </div>

          <div class="pricing-flow-pane pricing-flow-pane--checkout">
            <section v-if="selectedCheckoutOffer" class="pricing-section pricing-checkout-section" :aria-label="tt('billing.checkout', 'Checkout')">
              <button
                type="button"
                class="btn btn-outline-primary btn-round btn-sm checkout-back"
                @click="closeCheckout"
                :disabled="loading"
              >
                <i class="ti-arrow-left"></i> {{ tt("billing.backToPackages", "Volver a planes") }}
              </button>

              <div class="checkout-brand-badge" aria-label="Clipr">
                <img
                  :src="brandFavicon"
                  alt=""
                  class="checkout-brand-mark"
                  aria-hidden="true"
                />
                <span>Clipr</span>
              </div>

              <div class="checkout-shell">
                <div class="checkout-order-card">
                  <div class="checkout-order-card-glow" aria-hidden="true"></div>
                  <div class="checkout-order-card-inner">
                    <h3 class="checkout-panel-title">
                      <i class="ti-receipt" aria-hidden="true"></i>
                      {{ checkoutSummaryTitle }}
                    </h3>
                    <div class="checkout-order-hero">
                      <div class="checkout-plan-badge">
                        <i class="ti-crown" aria-hidden="true"></i>
                        <span>{{ selectedCheckoutOffer.name }}</span>
                      </div>
                      <div
                        class="checkout-price-display"
                        :class="{ 'checkout-price-display--discounted': hasAppliedPromoDiscount }"
                      >
                        <span
                          v-if="checkoutOriginalPriceAmount"
                          class="checkout-price-original"
                        >
                          ${{ formatPrice(checkoutOriginalPriceAmount) }}
                        </span>
                        <span class="checkout-price-amount">${{ formatPrice(checkoutPriceAmount) }}</span>
                        <span class="checkout-price-currency">USD</span>
                      </div>
                      <p
                        v-if="promoCompactMessage"
                        class="checkout-promo-message checkout-promo-message--compact"
                        :class="promoStatus === 'valid' ? 'checkout-promo-message--ok' : 'checkout-promo-message--error'"
                      >
                        <i :class="promoStatus === 'valid' ? 'ti-gift' : 'ti-info-alt'" aria-hidden="true"></i>
                        {{ promoCompactMessage }}
                      </p>
                    </div>
                    <p class="checkout-order-meta">
                      <i class="ti-package" aria-hidden="true"></i>
                      {{ tt("billing.unlimitedFeatureTokens", "720 tokens al día") }}
                    </p>
                    <p v-if="isAnnualCheckout && annualEquivalentLabel" class="checkout-order-meta">
                      {{ annualEquivalentLabel }}
                    </p>

                    <div
                      class="checkout-promo-card"
                      :class="{
                        'checkout-promo-card--valid': promoStatus === 'valid',
                        'checkout-promo-card--invalid': promoStatus === 'invalid',
                        'checkout-promo-card--checking': promoStatus === 'checking',
                      }"
                    >
                      <div class="checkout-promo-card-glow" aria-hidden="true"></div>
                      <div class="checkout-promo-card-body">
                        <p class="checkout-promo-title">{{ tt("billing.promoLabel", "¿Tienes un código promocional?") }}</p>

                        <div v-if="promoStatus === 'valid'" class="checkout-promo-applied">
                          <span class="checkout-promo-code-chip">{{ appliedPromoCode }}</span>
                          <button
                            type="button"
                            class="checkout-promo-remove"
                            :disabled="loading"
                            @click="clearPromo"
                          >
                            <i class="ti-close" aria-hidden="true"></i>
                            {{ tt("billing.promoRemove", "Quitar") }}
                          </button>
                        </div>

                        <div v-else class="checkout-promo-row">
                          <div class="checkout-promo-input-wrap">
                            <i class="ti-tag checkout-promo-input-icon" aria-hidden="true"></i>
                            <input
                              id="pricing-promo-input"
                              v-model="promoCode"
                              type="text"
                              autocomplete="off"
                              class="form-control checkout-promo-input"
                              :class="{ 'form-control--invalid': promoStatus === 'invalid' }"
                              :placeholder="tt('billing.promoPlaceholder', 'Ingresa tu código')"
                              :disabled="loading || promoStatus === 'checking'"
                              @input="onPromoInput"
                              @keyup.enter="applyPromoCode"
                            />
                          </div>
                          <button
                            type="button"
                            class="btn checkout-promo-btn"
                            :disabled="loading || promoStatus === 'checking' || !promoCode.trim()"
                            @click="applyPromoCode"
                          >
                            <i v-if="promoStatus === 'checking'" class="ti-reload ti-spin checkout-promo-btn-icon" aria-hidden="true"></i>
                            {{ promoStatus === "checking" ? tt("billing.promoChecking", "Validando...") : tt("billing.promoApply", "Aplicar") }}
                          </button>
                        </div>

                      </div>
                    </div>

                    <div class="checkout-pro-benefits">
                      <p class="checkout-pro-benefits-title">{{ tt("billing.proBenefitsTitle", "Beneficios desbloqueados") }}</p>
                      <ul class="checkout-pro-benefits-list">
                        <li
                          v-for="benefit in proBenefits"
                          :key="benefit.title"
                          class="checkout-pro-benefit-item"
                        >
                          <span class="checkout-benefit-check" aria-hidden="true">
                            <i class="ti-check"></i>
                          </span>
                          <span><strong>{{ benefit.title }}</strong> {{ benefit.body }}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div class="checkout-payment-card">
                  <div class="checkout-payment-card-glow" aria-hidden="true"></div>
                  <div class="checkout-payment-card-inner">
                  <h3 class="checkout-panel-title">
                    <i class="ti-credit-card" aria-hidden="true"></i>
                    {{ paymentFormTitle }}
                  </h3>
                  <div class="row checkout-payment-form">
                    <div class="col-md-12 mb-2 checkout-field checkout-field--full">
                      <label
                        class="text-muted d-block mb-1"
                        :class="{ 'checkout-field-label--invalid': isCardFieldInvalid('country') }"
                      >
                        {{ tt("billing.country", "País") }}
                      </label>
                      <div class="country-search">
                        <input
                          ref="countrySearchInput"
                          v-model="countrySearch"
                          name="clipr-contry"
                          type="text"
                          class="form-control"
                          :class="{
                            'form-control--invalid': isCardFieldInvalid('country'),
                            'country-search-input--selected': !!cardForm.country,
                          }"
                          :placeholder="tt('billing.searchCountry', 'Buscar tu país')"
                          autocomplete="off"
                          :aria-invalid="isCardFieldInvalid('country') ? 'true' : 'false'"
                          :readonly="!!cardForm.country"
                          :disabled="loading"
                          @focus="openCountryMenu"
                          @click="openCountryMenu"
                          @input="onCountrySearchInput"
                          @blur="onCountrySearchBlur"
                        />
                        <button
                          v-if="cardForm.country"
                          type="button"
                          class="country-search-clear"
                          aria-label="Clear selected country"
                          :disabled="loading"
                          @mousedown.prevent
                          @click.stop="clearSelectedCountry"
                        >
                          <i class="ti-close" aria-hidden="true"></i>
                        </button>
                        <div
                          v-if="showCountryMenu && filteredCountryOptions.length"
                          class="country-search-menu"
                        >
                          <button
                            v-for="country in filteredCountryOptions"
                            :key="country.code"
                            type="button"
                            class="country-search-option"
                            @mousedown.prevent="selectCountry(country)"
                          >
                            <span>{{ country.name }}</span>
                            <span class="country-search-code">{{ country.code }}</span>
                          </button>
                        </div>
                      </div>
                      <p v-if="getVisibleCardFieldError('country')" class="checkout-field-error">
                        {{ getVisibleCardFieldError("country") }}
                      </p>
                    </div>
                    <div class="col-md-12 mb-2 checkout-field checkout-field--full">
                      <label
                        class="text-muted d-block mb-1"
                        :class="{ 'checkout-field-label--invalid': isCardFieldInvalid('phone') }"
                      >
                        {{ tt("billing.phone", "Phone") }}
                      </label>
                      <input
                        v-model="cardForm.phone"
                        name="phone"
                        type="tel"
                        class="form-control"
                        :class="{ 'form-control--invalid': isCardFieldInvalid('phone') }"
                        autocomplete="tel"
                        maxlength="20"
                        inputmode="tel"
                        :placeholder="tt('billing.phonePlaceholder', '+525512345678')"
                        :aria-invalid="isCardFieldInvalid('phone') ? 'true' : 'false'"
                        @input="onPhoneInput"
                        :disabled="loading"
                      />
                      <p v-if="getVisibleCardFieldError('phone')" class="checkout-field-error">
                        {{ getVisibleCardFieldError("phone") }}
                      </p>
                    </div>
                    <div class="col-md-12 checkout-field checkout-field--full">
                      <hr class="checkout-form-divider" aria-hidden="true" />
                    </div>
                    <div class="col-md-12 mb-2 checkout-field checkout-field--full">
                      <label
                        class="text-muted d-block mb-1"
                        :class="{ 'checkout-field-label--invalid': isCardFieldInvalid('card_name') }"
                      >
                        {{ tt("billing.nameOnCard", "Nombre en la tarjeta") }}
                      </label>
                      <input
                        v-model="cardForm.card_name"
                        name="card_name"
                        type="text"
                        class="form-control"
                        :class="{ 'form-control--invalid': isCardFieldInvalid('card_name') }"
                        autocomplete="cc-name"
                        maxlength="120"
                        :placeholder="tt('billing.nameOnCardPlaceholder', 'ej. John Smith')"
                        :aria-invalid="isCardFieldInvalid('card_name') ? 'true' : 'false'"
                        :disabled="loading"
                      />
                      <p v-if="getVisibleCardFieldError('card_name')" class="checkout-field-error">
                        {{ getVisibleCardFieldError("card_name") }}
                      </p>
                    </div>
                    <div class="col-md-12 mb-2 checkout-field checkout-field--full">
                      <label
                        class="text-muted d-block mb-1"
                        :class="{ 'checkout-field-label--invalid': isCardFieldInvalid('card_number') }"
                      >
                        {{ tt("billing.cardNumber", "Número de tarjeta") }}
                      </label>
                      <div class="card-number-field">
                        <input
                          v-model="cardForm.card_number"
                          name="card_number"
                          type="text"
                          class="form-control"
                          :class="{ 'form-control--invalid': isCardFieldInvalid('card_number') }"
                          autocomplete="cc-number"
                          inputmode="numeric"
                          :maxlength="cardNumberMaxLength"
                          :placeholder="tt('billing.cardNumberPlaceholder', 'ej. 4242424242424242')"
                          :aria-invalid="isCardFieldInvalid('card_number') ? 'true' : 'false'"
                          @input="onCardNumberInput"
                          :disabled="loading"
                        />
                        <span
                          v-if="detectedCardBrandIconClass"
                          class="card-brand-indicator"
                          :class="`card-brand-indicator--${detectedCardBrand}`"
                          :aria-label="detectedCardBrandLabel"
                          :title="detectedCardBrandLabel"
                          role="img"
                        >
                          <i :class="detectedCardBrandIconClass" aria-hidden="true"></i>
                        </span>
                      </div>
                      <p v-if="getVisibleCardFieldError('card_number')" class="checkout-field-error">
                        {{ getVisibleCardFieldError("card_number") }}
                      </p>
                    </div>
                    <div class="col-md-3 mb-2 checkout-field checkout-field--compact">
                      <label
                        class="text-muted d-block mb-1"
                        :class="{ 'checkout-field-label--invalid': isCardFieldInvalid('exp_mmyy') }"
                      >
                        {{ tt("billing.expirationDate", "Fecha de expiración") }}
                      </label>
                      <input
                        v-model="cardForm.exp_mmyy"
                        name="exp_mmyy"
                        type="text"
                        class="form-control checkout-exp-input"
                        :class="{ 'form-control--invalid': isCardFieldInvalid('exp_mmyy') }"
                        inputmode="numeric"
                        maxlength="5"
                        placeholder="MM/YY"
                        :aria-invalid="isCardFieldInvalid('exp_mmyy') ? 'true' : 'false'"
                        @input="onExpMmyyInput"
                        :disabled="loading"
                      />
                      <p v-if="getVisibleCardFieldError('exp_mmyy')" class="checkout-field-error">
                        {{ getVisibleCardFieldError("exp_mmyy") }}
                      </p>
                    </div>
                    <div class="col-md-3 mb-2 checkout-field checkout-field--compact">
                      <label
                        class="text-muted d-block mb-1"
                        :class="{ 'checkout-field-label--invalid': isCardFieldInvalid('cvc') }"
                      >
                        {{ tt("billing.cvv", "CVV") }}
                      </label>
                      <input
                        v-model="cardForm.cvc"
                        name="cvc"
                        type="password"
                        class="form-control"
                        :class="{ 'form-control--invalid': isCardFieldInvalid('cvc') }"
                        autocomplete="cc-csc"
                        inputmode="numeric"
                        :maxlength="cardCvcMaxLength"
                        placeholder="123"
                        :aria-invalid="isCardFieldInvalid('cvc') ? 'true' : 'false'"
                        @input="onCvcInput"
                        :disabled="loading"
                      />
                      <p v-if="getVisibleCardFieldError('cvc')" class="checkout-field-error">
                        {{ getVisibleCardFieldError("cvc") }}
                      </p>
                    </div>
                  </div>
                  <p v-if="cardFormError && !hasVisibleCardFieldErrors()" class="checkout-form-error">{{ cardFormError }}</p>
                  <div class="checkout-pay-cta">
                    <button
                      type="button"
                      class="btn btn-primary btn-round w-100 checkout-pay-btn"
                      :class="{ 'checkout-pay-btn--subscription': isSubscriptionCheckout && !isUpgradeCheckout }"
                      @click="submitCheckout"
                      :disabled="loading"
                    >
                      <span class="checkout-pay-btn-shine" aria-hidden="true"></span>
                      <span v-if="loading && ['subscription:submit', 'subscription:upgrade', 'subscription:update_payment_method'].includes(loadingActionKey)">
                        <i class="ti-reload ti-spin"></i> {{ tt("billing.processing", "Procesando...") }}
                      </span>
                      <span v-else-if="paymentMethodUpdateMode">
                        {{ checkoutActionLabel }}
                      </span>
                      <span v-else>
                        {{ checkoutActionLabel }}
                        ${{ selectedCheckoutOffer ? formatPrice(checkoutPriceAmount) : "0.00" }} USD
                      </span>
                    </button>
                    <p class="checkout-legal text-muted mb-0">
                      <i class="ti-lock" aria-hidden="true"></i>
                      {{ tt("billing.cardEncrypted", "La información de tu tarjeta está cifrada y nunca se almacena.") }}
                    </p>
                  </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
    <body-portal>
      <div
        v-if="showCancellationModal"
        class="confirm-modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-cancel-title"
        @click.self="closeCancellationModal"
      >
        <form class="confirm-modal pricing-cancellation-modal" @submit.prevent="cancelSubscription">
          <div class="confirm-modal-header confirm-modal-header--centered">
            <h3 id="billing-cancel-title" class="confirm-modal-title mb-0">
              {{ tt("billing.cancelSubscriptionTitle", "Cancelar suscripción") }}
            </h3>
            <button type="button" class="confirm-modal-close" @click="closeCancellationModal">
              <i class="ti-close"></i>
            </button>
          </div>
          <div class="confirm-modal-body">
            <p class="confirm-modal-subtitle pricing-cancellation-modal__intro">
              {{ tt("billing.cancelSubscriptionPrompt", "Tu opinión nos ayuda a mejorar Clipr.") }}
            </p>
            <label class="pricing-cancellation-modal__field">
              <span>{{ tt("billing.cancelReasonLabel", "Motivo de cancelación") }}</span>
              <select v-model="cancellationReasonType" required>
                <option value="" disabled>{{ tt("billing.cancelReasonPlaceholder", "Selecciona un motivo") }}</option>
                <option value="price">{{ tt("billing.cancelReasonPrice", "El precio es muy alto") }}</option>
                <option value="usage">{{ tt("billing.cancelReasonUsage", "No uso Clipr lo suficiente") }}</option>
                <option value="features">{{ tt("billing.cancelReasonFeatures", "No encontré las funciones que necesito") }}</option>
                <option value="technical">{{ tt("billing.cancelReasonTechnical", "Tuve problemas técnicos") }}</option>
                <option value="other">{{ tt("billing.cancelReasonOther", "Otro motivo") }}</option>
              </select>
            </label>
            <label class="pricing-cancellation-modal__field">
              <span>{{ tt("billing.cancelMessageLabel", "Cuéntanos más") }}</span>
              <textarea
                v-model.trim="cancellationMessage"
                rows="4"
                maxlength="1000"
                required
                :placeholder="tt('billing.cancelMessagePlaceholder', 'Escribe aquí tu motivo de cancelación')"
              ></textarea>
            </label>
            <p v-if="cancellationFormError" class="pricing-cancellation-modal__error">
              {{ cancellationFormError }}
            </p>
          </div>
          <div class="confirm-modal-footer">
            <button type="button" class="confirm-modal-btn confirm-modal-btn--secondary" @click="closeCancellationModal">
              {{ tt("common.cancel", "Cancelar") }}
            </button>
            <button type="submit" class="confirm-modal-btn confirm-modal-btn--danger" :disabled="loading">
              <span v-if="loading && loadingActionKey === 'subscription:cancel'">
                <i class="ti-reload ti-spin"></i> {{ tt("billing.cancellingSubscription", "Cancelando...") }}
              </span>
              <span v-else>{{ tt("billing.confirmCancellation", "Confirmar cancelación") }}</span>
            </button>
          </div>
        </form>
      </div>
    </body-portal>
    <body-portal>
      <div
        v-if="showSuccessModal"
        class="confirm-modal-overlay pricing-success-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-success-title"
        @click="closeSuccessModal"
      >
        <div
          :key="celebrationBurstKey"
          class="billing-celebration"
          aria-hidden="true"
        >
          <span
            v-for="piece in celebrationPieces"
            :key="piece.id"
            class="billing-celebration-piece"
            :style="piece.style"
          ></span>
        </div>
        <div class="confirm-modal pricing-success-modal" @click.stop>
          <div class="confirm-modal-header confirm-modal-header--centered">
            <h3 id="billing-success-title" class="confirm-modal-title pricing-success-title">
              {{ tt("billing.checkoutSuccessTitle", "Todo listo") }}
            </h3>
            <button type="button" class="confirm-modal-close" @click="closeSuccessModal">
              <i class="ti-close"></i>
            </button>
          </div>
          <div class="confirm-modal-body pricing-success-body">
            <p class="confirm-modal-subtitle pricing-success-copy mb-0">{{ successModalMessage }}</p>
          </div>
          <div class="confirm-modal-footer confirm-modal-footer--centered">
            <button type="button" class="confirm-modal-btn confirm-modal-btn--primary" @click="closeSuccessModal">
              {{ tt("common.close", "Cerrar") }}
            </button>
          </div>
        </div>
      </div>
    </body-portal>
    <body-portal>
      <div
        v-if="showRequestErrorModal"
        class="confirm-modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-request-error-title"
        @click="closeRequestErrorModal"
      >
        <div class="confirm-modal" @click.stop>
          <div class="confirm-modal-header confirm-modal-header--centered">
            <h3 id="billing-request-error-title" class="confirm-modal-title mb-0">
              {{ tt("billing.requestErrorTitle", "No pudimos completar tu solicitud") }}
            </h3>
            <button type="button" class="confirm-modal-close" @click="closeRequestErrorModal">
              <i class="ti-close"></i>
            </button>
          </div>
          <div class="confirm-modal-body">
            <p class="pricing-request-message mb-0">{{ requestErrorMessage }}</p>
          </div>
          <div class="confirm-modal-footer">
            <button type="button" class="confirm-modal-btn confirm-modal-btn--secondary" @click="closeRequestErrorModal">
              {{ tt("common.close", "Cerrar") }}
            </button>
          </div>
        </div>
      </div>
    </body-portal>
  </div>
</template>

<script>
import { supabase } from "@/supabase";
import {
  PLAN_SECTIONS,
} from "@/constants/planSections";
import { COUNTRY_OPTIONS } from "@/constants/countries";
import { useCurrentUserStore } from "@/stores/currentUser";
import { getApiUrl as resolveApiUrl } from "@/api/client";
import { createCancellationFeedback } from "@/api/suggestions";
import { buildSignupAttribution } from "@/api/session";
import { buildAuthHeaders } from "@/utils/impersonation";
import { trackMetaStandardEvent } from "@/utils/metaPixel";
import {
  isInternationalPhone,
  normalizeInternationalPhoneInput,
} from "@/utils/phone";

const brandFavicon = "/favicon.svg";
const PURCHASE_STATUS_POLL_INTERVAL_MS = 2000;
const SUBSCRIPTION_STATUS_POLL_INTERVAL_MS = 2000;
const CARD_FORM_FIELDS = ["country", "phone", "card_name", "card_number", "exp_mmyy", "cvc"];
const CANCELLATION_INFO_STORAGE_KEY = "clipr_ecartpay_cancellation_info";
const USE_DATABASE_SUBSCRIPTION_CATALOG = !["0", "false", "off", "no"].includes(
  String(process.env.VUE_APP_PRICING_USE_DATABASE_SUBSCRIPTIONS || "true").trim().toLowerCase()
);
const PERIOD_TYPE_TO_SECTION_ID = {
  monthly: "monthly",
  yearly: "annual",
};
const SECTION_ID_TO_PERIOD_TYPE = {
  monthly: "monthly",
  annual: "yearly",
};
const FALLBACK_SECTION_BY_ID = PLAN_SECTIONS.reduce((accumulator, section) => {
  accumulator[section.id] = section;
  return accumulator;
}, {});
const BILLING_CELEBRATION_PIECES = [
  {
    id: "piece-1",
    style: {
      "--billing-confetti-left": "6%",
      "--billing-confetti-phase": "-3.6s",
      "--billing-confetti-duration": "4.4s",
      "--billing-confetti-drift": "-26px",
      "--billing-confetti-rotate": "300deg",
      "--billing-confetti-color": "#a855f7",
    },
  },
  {
    id: "piece-2",
    style: {
      "--billing-confetti-left": "12%",
      "--billing-confetti-phase": "-1.9s",
      "--billing-confetti-duration": "4.1s",
      "--billing-confetti-drift": "22px",
      "--billing-confetti-rotate": "260deg",
      "--billing-confetti-color": "#f472b6",
    },
  },
  {
    id: "piece-3",
    style: {
      "--billing-confetti-left": "19%",
      "--billing-confetti-phase": "-0.8s",
      "--billing-confetti-duration": "3.9s",
      "--billing-confetti-drift": "-18px",
      "--billing-confetti-rotate": "320deg",
      "--billing-confetti-color": "#4ade80",
    },
  },
  {
    id: "piece-4",
    style: {
      "--billing-confetti-left": "27%",
      "--billing-confetti-phase": "-2.7s",
      "--billing-confetti-duration": "4.6s",
      "--billing-confetti-drift": "16px",
      "--billing-confetti-rotate": "280deg",
      "--billing-confetti-color": "#facc15",
    },
  },
  {
    id: "piece-5",
    style: {
      "--billing-confetti-left": "34%",
      "--billing-confetti-phase": "-1.3s",
      "--billing-confetti-duration": "4.2s",
      "--billing-confetti-drift": "-32px",
      "--billing-confetti-rotate": "360deg",
      "--billing-confetti-color": "#38bdf8",
    },
  },
  {
    id: "piece-6",
    style: {
      "--billing-confetti-left": "41%",
      "--billing-confetti-phase": "-4.0s",
      "--billing-confetti-duration": "4.8s",
      "--billing-confetti-drift": "14px",
      "--billing-confetti-rotate": "250deg",
      "--billing-confetti-color": "#fb7185",
    },
  },
  {
    id: "piece-7",
    style: {
      "--billing-confetti-left": "48%",
      "--billing-confetti-phase": "-2.2s",
      "--billing-confetti-duration": "4.0s",
      "--billing-confetti-drift": "-14px",
      "--billing-confetti-rotate": "280deg",
      "--billing-confetti-color": "#818cf8",
    },
  },
  {
    id: "piece-8",
    style: {
      "--billing-confetti-left": "54%",
      "--billing-confetti-phase": "-0.4s",
      "--billing-confetti-duration": "4.5s",
      "--billing-confetti-drift": "24px",
      "--billing-confetti-rotate": "330deg",
      "--billing-confetti-color": "#f59e0b",
    },
  },
  {
    id: "piece-9",
    style: {
      "--billing-confetti-left": "61%",
      "--billing-confetti-phase": "-3.0s",
      "--billing-confetti-duration": "4.3s",
      "--billing-confetti-drift": "-20px",
      "--billing-confetti-rotate": "290deg",
      "--billing-confetti-color": "#22c55e",
    },
  },
  {
    id: "piece-10",
    style: {
      "--billing-confetti-left": "68%",
      "--billing-confetti-phase": "-1.6s",
      "--billing-confetti-duration": "3.8s",
      "--billing-confetti-drift": "18px",
      "--billing-confetti-rotate": "340deg",
      "--billing-confetti-color": "#e879f9",
    },
  },
  {
    id: "piece-11",
    style: {
      "--billing-confetti-left": "76%",
      "--billing-confetti-phase": "-3.5s",
      "--billing-confetti-duration": "4.7s",
      "--billing-confetti-drift": "-12px",
      "--billing-confetti-rotate": "275deg",
      "--billing-confetti-color": "#2dd4bf",
    },
  },
  {
    id: "piece-12",
    style: {
      "--billing-confetti-left": "84%",
      "--billing-confetti-phase": "-2.5s",
      "--billing-confetti-duration": "4.1s",
      "--billing-confetti-drift": "28px",
      "--billing-confetti-rotate": "315deg",
      "--billing-confetti-color": "#fb7185",
    },
  },
  {
    id: "piece-13",
    style: {
      "--billing-confetti-left": "91%",
      "--billing-confetti-phase": "-0.9s",
      "--billing-confetti-duration": "4.4s",
      "--billing-confetti-drift": "-24px",
      "--billing-confetti-rotate": "350deg",
      "--billing-confetti-color": "#c084fc",
    },
  },
];

export default {
  name: "Billing",
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    initialCheckoutCode: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      brandFavicon,
      billingStatus: null,
      billingLoaded: false,
      subscriptionStatus: null,
      subscriptionLoaded: false,
      subscriptionPoller: null,
      shouldPollSubscriptionStatus: false,
      pendingSubscription: null,
      pendingPurchase: null,
      pendingPurchasePoller: null,
      useDatabaseSubscriptionCatalog: USE_DATABASE_SUBSCRIPTION_CATALOG,
      planSections: PLAN_SECTIONS,
      selectedOfferCode: "unlimited_monthly",
      checkoutMode: false,
      selectedSubscription: null,
      showCountryMenu: false,
      countrySearch: "",
      cardForm: {
        country: "",
        phone: "",
        card_name: "",
        card_number: "",
        exp_mmyy: "",
        cvc: "",
      },
      cardFormError: "",
      showCardFieldValidation: false,
      loading: false,
      loadingActionKey: null,
      billingCycle: "monthly",
      cancellationResult: null,
      showCancellationModal: false,
      cancellationReasonType: "",
      cancellationMessage: "",
      cancellationFormError: "",
      paymentMethodUpdateMode: false,
      showSuccessModal: false,
      successModalMessage: "",
      showRequestErrorModal: false,
      requestErrorMessage: "",
      celebrationBurstKey: 0,
      celebrationPieces: BILLING_CELEBRATION_PIECES,
      embeddedCheckoutCode: "",
      promoCode: "",
      promoStatus: "idle",
      promoMessage: "",
      appliedPromoCode: "",
      appliedPromoDiscountPercent: 0,
      appliedPromoBonusTokens: 0,
    };
  },
  computed: {
    cardNumberDigits() {
      return String(this.cardForm?.card_number || "").replace(/\D/g, "");
    },
    isAmexCard() {
      return /^3[47]/.test(this.cardNumberDigits);
    },
    isSpanish() {
      return String(this.$i18n?.locale || "").toLowerCase().startsWith("es");
    },
    catalogOffers() {
      return (this.planSections || []).reduce((offers, section) => offers.concat(section.plans || []), []);
    },
    selectedOffer() {
      return this.catalogOffers.find((offer) => offer.code === this.selectedOfferCode) || null;
    },
    selectedCheckoutOffer() {
      return this.selectedSubscription || null;
    },
    unlimitedMonthlyOffer() {
      return this.getOfferByCode("unlimited_monthly");
    },
    unlimitedAnnualOffer() {
      return this.getOfferByCode("unlimited_annual");
    },
    unlimitedOffer() {
      if (this.billingCycle === "annual") {
        return this.unlimitedAnnualOffer || this.unlimitedMonthlyOffer;
      }
      return this.unlimitedMonthlyOffer || this.unlimitedAnnualOffer;
    },
    annualMonthlyEquivalent() {
      const price = Number(this.unlimitedAnnualOffer?.price);
      if (!Number.isFinite(price) || price <= 0) return null;
      return price / 12;
    },
    annualEquivalentLabel() {
      if (this.annualMonthlyEquivalent == null) return "";
      return this.tt("billing.annualEquivalentPerMonth", "Equivale a ${amount} USD/mes")
        .replace("{amount}", this.formatPrice(this.annualMonthlyEquivalent));
    },
    annualSavingsPercent() {
      const annualPrice = Number(this.unlimitedAnnualOffer?.price);
      const monthlyPrice = Number(this.unlimitedMonthlyOffer?.price);
      if (!Number.isFinite(annualPrice) || !Number.isFinite(monthlyPrice) || monthlyPrice <= 0) {
        return null;
      }
      const fullYearPrice = monthlyPrice * 12;
      if (annualPrice >= fullYearPrice - 0.0001) return null;
      const pct = Math.round(((fullYearPrice - annualPrice) / fullYearPrice) * 100);
      return pct > 0 ? pct : null;
    },
    annualSavingsLabel() {
      if (!this.annualSavingsPercent) return "";
      return this.tt("billing.annualSavingsVsMonthly", "Ahorra {percent}% vs el plan mensual")
        .replace("{percent}", String(this.annualSavingsPercent));
    },
    isAnnualCheckout() {
      return this.isAnnualPlan(this.selectedSubscription);
    },
    isFreePlanCurrent() {
      return this.subscriptionLoaded && !this.hasSubscription;
    },
    freePlanIncludedFeatures() {
      return [
        this.tt("billing.freeFeatureTokens", "240 tokens al mes"),
        this.tt("billing.freeFeatureFirstStream", "Primer stream gratis de hasta 4 horas"),
      ];
    },
    freePlanExcludedFeatures() {
      return [
        this.tt("billing.freeFeatureWatermark", "Clips con marca de agua"),
        this.tt("billing.freeFeatureNoSocials", "Sin conexión de redes sociales"),
        this.tt("billing.freeFeatureNoSchedule", "Sin programación de publicaciones"),
        this.tt("billing.freeFeatureNoAi", "Sin análisis con IA"),
        this.tt("billing.freeFeatureNoEditor", "Sin editor de clips"),
      ];
    },
    unlimitedPlanFeatures() {
      return [
        { label: this.tt("billing.unlimitedFeatureTokens", "720 tokens al día") },
        { label: this.tt("billing.unlimitedFeatureNoWatermark", "Sin marca de agua") },
        { label: this.tt("billing.unlimitedFeatureSocials", "Conecta tus redes sociales") },
        { label: this.tt("billing.unlimitedFeatureSchedule", "Programa tus publicaciones") },
        { label: this.tt("billing.unlimitedFeatureAi", "Análisis de streams con IA") },
        { label: this.tt("billing.unlimitedFeatureEditor", "Editor de clips PRO") },
        {
          label: this.tt("billing.unlimitedFeatureAnalytics", "Analytics de posts a 1 y 7 días"),
          comingSoon: true,
        },
      ];
    },
    hasAppliedPromoDiscount() {
      return this.promoStatus === "valid" && Number(this.appliedPromoDiscountPercent || 0) > 0;
    },
    checkoutBasePriceAmount() {
      const amount = Number(this.selectedCheckoutOffer?.price);
      return Number.isFinite(amount) ? amount : 0;
    },
    checkoutPriceAmount() {
      const amount = this.checkoutBasePriceAmount;
      const discount = Number(this.appliedPromoDiscountPercent || 0);
      if (this.promoStatus !== "valid" || !Number.isFinite(discount) || discount <= 0) {
        return amount;
      }
      return Math.max(0, Number((amount * ((100 - discount) / 100)).toFixed(2)));
    },
    checkoutOriginalPriceAmount() {
      if (this.hasAppliedPromoDiscount) {
        return this.checkoutBasePriceAmount;
      }
      return this.planListPriceAtBaseRate(this.selectedCheckoutOffer);
    },
    promoCompactMessage() {
      if (!this.promoMessage) return "";
      if (this.promoStatus === "valid" && this.appliedPromoDiscountPercent > 0) {
        return this.tt("billing.promoAppliedCompact", "{percent}% aplicado")
          .replace("{percent}", this.appliedPromoDiscountPercent);
      }
      return this.promoMessage;
    },
    isSubscriptionCheckout() {
      return !!this.selectedSubscription;
    },
    checkoutSummaryTitle() {
      if (this.paymentMethodUpdateMode) {
        return this.tt("billing.paymentMethodUpdateSummary", "Cuidemos tu suscripción");
      }
      return this.tt("billing.orderSummary", "Resumen de orden");
    },
    paymentFormTitle() {
      if (this.paymentMethodUpdateMode) {
        return this.tt("billing.updatePaymentMethod", "Actualizar método de pago");
      }
      return this.tt("billing.payment", "Payment Details");
    },
    detectedCardBrand() {
      return this.detectCardBrand(this.cardForm.card_number);
    },
    detectedCardBrandLabel() {
      if (this.detectedCardBrand === "amex") return "American Express";
      if (this.detectedCardBrand === "visa") return "Visa";
      if (this.detectedCardBrand === "mastercard") return "Mastercard";
      return "";
    },
    detectedCardBrandIconClass() {
      if (this.detectedCardBrand === "amex") return "fa-brands fa-cc-amex";
      if (this.detectedCardBrand === "visa") return "fa-brands fa-cc-visa";
      if (this.detectedCardBrand === "mastercard") return "fa-brands fa-cc-mastercard";
      return "";
    },
    cardNumberMaxLength() {
      return this.detectedCardBrand === "amex" ? 15 : 16;
    },
    cardCvcMaxLength() {
      return this.detectedCardBrand === "amex" ? 4 : 3;
    },
    canUpgradeSubscription() {
      return this.hasSubscription && this.currentSubscriptionStatus !== "canceled";
    },
    hasActiveSubscriptionForPlanPurchase() {
      return this.hasSubscription && this.currentSubscriptionStatus !== "canceled";
    },
    isUpgradeCheckout() {
      if (!this.isSubscriptionCheckout || !this.selectedSubscription) return false;
      return this.canUpgradeSubscription && !this.isCurrentSubscriptionPlan(this.selectedSubscription);
    },
    checkoutActionLabel() {
      if (this.paymentMethodUpdateMode) {
        return this.tt("billing.updatePaymentMethodCta", "Guardar tarjeta e intentar cobro");
      }
      if (this.isSubscriptionCheckout) {
        if (this.isUpgradeCheckout) {
          return this.tt("billing.upgradeSubscription", "Cambiar suscripción");
        }
        return this.tt("billing.startSubscription", "Iniciar suscripción");
      }
      return this.tt("billing.pay", "Pagar");
    },
    proBenefits() {
      return [
        {
          title: this.$t("tryClips.paywall.benefits.perfectClipTitle"),
          body: this.$t("tryClips.paywall.benefits.perfectClipBody"),
        },
        {
          title: this.$t("tryClips.paywall.benefits.brandEndTitle"),
          body: this.$t("tryClips.paywall.benefits.brandEndBody"),
        },
        {
          title: this.$t("tryClips.paywall.benefits.noWatermarkTitle"),
          body: this.$t("tryClips.paywall.benefits.noWatermarkBody"),
        },
        {
          title: this.$t("tryClips.paywall.benefits.publishTitle"),
          body: this.$t("tryClips.paywall.benefits.publishBody"),
        },
      ];
    },
    creditsRemaining() {
      const raw = this.billingStatus?.credits_remaining;
      if (raw === null || raw === undefined || Number.isNaN(Number(raw))) return 0;
      return Number(raw);
    },
    renderHoursAvailableText() {
      const hours = this.creditsRemaining / 60;
      const locale = this.isSpanish ? "es-MX" : "en-US";
      const formattedHours = new Intl.NumberFormat(locale, {
        maximumFractionDigits: 2,
      }).format(hours);
      return this.tt("billing.renderHoursAvailable", "aprox. {hours} h de render")
        .replace("{hours}", formattedHours);
    },
    tokenBreakdown() {
      const expiring = this.toFiniteNumber(this.billingStatus?.subscription_credits);
      const payg = this.toFiniteNumber(this.billingStatus?.payg_credits);
      const activeLotsCount = this.toFiniteNumber(this.billingStatus?.active_lots_count);
      const expiringLotsCount = this.toFiniteNumber(this.cancellationResult?.strict_expiry_lots_count);
      const hasData = expiring != null || payg != null;
      return {
        hasData,
        expiring: expiring != null ? expiring : 0,
        payg: payg != null ? payg : 0,
        expiringLotsCount,
        activeLotsCount,
      };
    },
    expiringTokensDate() {
      if (this.billingStatus?.subscription_credits_expires_at) {
        return this.billingStatus.subscription_credits_expires_at;
      }
      if (this.cancellationResult?.tokens_usable_until) {
        return this.cancellationResult.tokens_usable_until;
      }
      return this.subscriptionStatus?.next_payment_date || null;
    },
    cancellationAccessUntil() {
      return this.cancellationResult?.benefits_end_at
        || this.cancellationResult?.tokens_usable_until
        || this.cancellationResult?.next_payment_date
        || null;
    },
    isCancellationScheduled() {
      if (!this.subscriptionStatus?.cancel_at_period_end) return false;
      return this.toTimestamp(this.subscriptionStatus?.benefits_end_at) > Date.now();
    },
    subscriptionStatusDate() {
      return this.subscriptionStatus?.benefits_end_at
        || this.subscriptionStatus?.payment_grace_ends_at
        || this.subscriptionStatus?.next_payment_date
        || this.subscriptionStatus?.end_date
        || null;
    },
    subscriptionStatusDateLabel() {
      if (this.isCancellationScheduled) {
        return this.tt("billing.subscriptionBenefitsActiveUntil", "Acceso activo hasta {date}");
      }
      if (this.currentSubscriptionStatus === "canceled") {
        return this.tt("billing.subscriptionExpiresOn", "Expira el {date}");
      }
      if (this.localSubscriptionStatus === "payment_failed") {
        return this.tt("billing.paymentGraceEndsOn", "Periodo de gracia hasta {date}");
      }
      return this.tt("billing.subscriptionRenewsOn", "Renueva el {date}");
    },
    hasSubscription() {
      return !!this.subscriptionStatus?.subscription_id;
    },
    localSubscriptionStatus() {
      const status = String(this.subscriptionStatus?.local_status || "").toLowerCase();
      return status === "cancelled" ? "canceled" : status;
    },
    providerSubscriptionStatus() {
      const status = String(this.subscriptionStatus?.provider_status || "").toLowerCase();
      return status === "cancelled" ? "canceled" : status;
    },
    subscriptionRiskStatus() {
      if (["payment_failed", "paused"].includes(this.localSubscriptionStatus)) {
        return this.localSubscriptionStatus;
      }
      return "";
    },
    currentSubscriptionStatus() {
      if (this.isCancellationScheduled) {
        return "canceled";
      }
      if (!this.localSubscriptionStatus) {
        return this.providerSubscriptionStatus;
      }
      if (this.localSubscriptionStatus === "payment_failed") {
        return "active";
      }
      return this.localSubscriptionStatus;
    },
    currentSubscriptionStatusLabel() {
      const status = this.currentSubscriptionStatus;
      if (status === "active") return this.tt("billing.subscriptionActive", "Activa");
      if (status === "pending") return this.tt("billing.subscriptionPending", "Pendiente");
      if (status === "paused") return this.tt("billing.subscriptionPaused", "Pausada");
      if (status === "canceled") return this.tt("billing.subscriptionCanceled", "Cancelada");
      if (!status) return this.tt("billing.noActiveSubscription", "Sin suscripción activa");
      return status;
    },
    isSubscriptionPending() {
      return this.currentSubscriptionStatus === "pending";
    },
    currentSubscriptionOfferCode() {
      return String(this.subscriptionStatus?.plan_code || "").trim();
    },
    currentSubscriptionOffer() {
      return this.catalogOffers.find((offer) => offer.code === this.currentSubscriptionOfferCode) || null;
    },
    subscriptionDisplayName() {
      return this.subscriptionStatus?.display_name || this.currentSubscriptionOffer?.name || this.tt("billing.noActiveSubscription", "Sin suscripción activa");
    },
    canCancelSubscription() {
      return this.hasSubscription
        && !this.isCancellationScheduled
        && ["active", "pending"].includes(this.currentSubscriptionStatus);
    },
    canUpdatePaymentMethod() {
      return this.hasSubscription && ["payment_failed", "paused"].includes(this.subscriptionRiskStatus);
    },
    hasSubscriptionActions() {
      return this.canCancelSubscription || this.canUpdatePaymentMethod;
    },
    showSubscriptionIssueBanner() {
      return this.hasSubscription && !!this.subscriptionRiskStatus;
    },
    paymentGraceEndsAt() {
      return this.subscriptionStatus?.payment_grace_ends_at
        || useCurrentUserStore().entitlements?.payment_grace_ends_at
        || null;
    },
    subscriptionIssueBannerText() {
      if (this.subscriptionRiskStatus === "paused") {
        return this.tt(
          "billing.renewalPaymentIssuePaused",
          "Tu suscripción está en pausa porque no pudimos completar la renovación. Actualiza tu método de pago para reactivarla y mantener tus beneficios."
        );
      }
      if (this.paymentGraceEndsAt) {
        return this.tt(
          "billing.paymentGraceWarning",
          "Tu suscripción expira pronto porque el último cobro no se completó. Actualiza tu método de pago o asegúrate de tener saldo disponible antes del {date} para conservar tus beneficios."
        ).replace("{date}", this.formatLocalDate(this.paymentGraceEndsAt));
      }
      return this.tt(
        "billing.renewalPaymentIssueFailed",
        "No pudimos completar la renovación. Actualiza tu método de pago o confirma que tu tarjeta tenga saldo disponible para evitar perder tus beneficios."
      );
    },
    isLegacyPlan() {
      return this.hasSubscription && this.subscriptionStatus?.plan_exists_in_catalog === false;
    },
    legacyPlanBannerText() {
      return this.tt(
        "billing.legacyPlanBannerFallbackText",
        "Tu plan actual ya no está disponible para nuevas compras. Mantendrás tus beneficios actuales hasta que canceles o cambies de plan."
      );
    },
    pendingPurchaseLabel() {
      if (!this.pendingPurchase) return "";
      if (this.pendingPurchase.status === "confirmed") return this.tt("billing.confirmed", "Confirmada");
      if (this.pendingPurchase.status === "failed") return this.tt("billing.failed", "Fallida");
      return this.tt("billing.pendingConfirmation", "Confirmación pendiente");
    },
    countryOptions() {
      return COUNTRY_OPTIONS;
    },
    prioritizedCountryOptions() {
      const pinnedCountryCodes = ["US", "MX"];
      const pinnedCountries = pinnedCountryCodes
        .map((code) => this.countryOptions.find((country) => country.code === code))
        .filter(Boolean);
      const pinnedCountrySet = new Set(pinnedCountryCodes);
      const remainingCountries = this.countryOptions.filter((country) => !pinnedCountrySet.has(country.code));
      return pinnedCountries.concat(remainingCountries);
    },
    filteredCountryOptions() {
      const query = String(this.countrySearch || "").trim().toLowerCase();
      const countries = this.prioritizedCountryOptions;
      if (this.cardForm.country || !query) return countries.slice(0, 12);
      return countries
        .filter((country) => {
          const label = `${country.name} ${country.code}`.toLowerCase();
          return label.includes(query);
        })
        .slice(0, 12);
    },
  },
  async mounted() {
    try {
      this.restoreCancellationResult();
      if (this.embedded) {
        this.embeddedCheckoutCode = String(this.initialCheckoutCode || "").trim();
      }
      await this.loadCatalogBySourceFlag();
      this.applyCheckoutFromRoute();
      this.restorePendingPurchase();
      await this.refreshBillingStatusFromStore();

      if (this.pendingPurchase?.reference_id) {
        await this.checkPendingPurchaseNow();
        if (this.pendingPurchase?.reference_id) {
          this.startPendingPurchasePolling();
        }
        return;
      }

      await this.refreshSubscriptionStatus();
    } catch (error) {
      throw error;
    }
  },
  beforeDestroy() {
    this.stopPendingPurchasePolling();
    this.stopSubscriptionPolling();
  },
  watch: {
    "$route.query.checkout"(nextCheckout, previousCheckout) {
      if (this.embedded) return;
      this.applyCheckoutFromRoute();
      if (nextCheckout !== previousCheckout) {
        this.scrollToBillingTop();
      }
    },
    initialCheckoutCode(nextCode) {
      if (!this.embedded) return;
      this.embeddedCheckoutCode = String(nextCode || "").trim();
      this.applyCheckoutFromRoute();
    },
  },
  methods: {
    tt(key, fallback) {
      if (typeof this.$t !== "function") return fallback;
      // New copy may not exist in the i18n catalogs yet — fall back to the
      // provided Spanish default instead of rendering the raw key path.
      if (
        fallback != null
        && typeof this.$te === "function"
        && !this.$te(key)
        && !this.$te(key, "es")
      ) {
        return fallback;
      }
      return this.$t(key);
    },
    isAnnualPlan(plan) {
      if (!plan) return false;
      return plan.periodType === "yearly" || String(plan.code || "").endsWith("_annual");
    },
    planBillingPeriodLabel(plan) {
      if (this.isAnnualPlan(plan)) return this.tt("billing.year", "año");
      return this.tt("billing.month", "mes");
    },
    scrollToBillingTop() {
      this.$nextTick(() => {
        const billingPage = this.$refs.billingPage;
        if (billingPage && typeof billingPage.scrollIntoView === "function") {
          billingPage.scrollIntoView({ behavior: "auto", block: "start" });
        }
      });
    },
    getApiUrl() {
      return resolveApiUrl();
    },
    formatPrice(value) {
      if (!Number.isFinite(Number(value))) return "0.00";
      return Number(value).toFixed(2);
    },
    applyHardcodedSubscriptionCatalog() {
      this.planSections = PLAN_SECTIONS;
      this.ensureSelectedOfferCode();
    },
    async loadCatalogBySourceFlag() {
      if (!this.useDatabaseSubscriptionCatalog) {
        this.applyHardcodedSubscriptionCatalog();
        return;
      }
      await this.loadSubscriptionCatalog();
    },
    getSectionTemplate(sectionId) {
      return FALLBACK_SECTION_BY_ID[sectionId] || { id: sectionId, plans: [] };
    },
    getPlanTemplateByCode(code) {
      if (!code) return null;
      const allPlans = PLAN_SECTIONS.reduce(
        (plans, section) => plans.concat(section.plans || []),
        []
      );
      return allPlans.find((plan) => plan.code === code) || null;
    },
    getSubscriptionPlanCredits(plan, fallbackValue = 0) {
      const numeric = Number(plan?.credits ?? plan?.credits_per_period ?? fallbackValue);
      return Number.isFinite(numeric) ? numeric : Number(fallbackValue || 0);
    },
    getSubscriptionPlanDisplayPrice(plan, fallbackValue = 0) {
      const numeric = Number(
        plan?.price?.app
        ?? plan?.price?.landing
        ?? plan?.price?.original
        ?? fallbackValue
      );
      return Number.isFinite(numeric) ? numeric : Number(fallbackValue || 0);
    },
    buildSubscriptionOffer(plan, sectionId) {
      const backendPlanCode = String(plan?.plan_code || "").trim();
      const template = this.getPlanTemplateByCode(backendPlanCode);
      const description = String(plan?.description || template?.description || "").trim();

      return {
        code: backendPlanCode,
        backendPlanCode,
        periodType: SECTION_ID_TO_PERIOD_TYPE[sectionId] || "monthly",
        name: String(plan?.display_name || template?.name || backendPlanCode),
        price: this.getSubscriptionPlanDisplayPrice(plan, template?.price || 0),
        priceOriginal: Number(plan?.price?.original),
        priceLanding: Number(plan?.price?.landing),
        priceApp: Number(plan?.price?.app),
        tokens: this.getSubscriptionPlanCredits(plan, template?.tokens || 0),
        purchaseType: "subscription",
        description,
        badge: template?.badge || "",
        featured: !!template?.featured,
      };
    },
    buildSubscriptionSection(sectionId, plans) {
      const template = this.getSectionTemplate(sectionId);
      const orderedTemplateCodes = (template.plans || []).map((plan) => plan.code);
      const mappedPlans = (plans || [])
        .filter((plan) => plan && (plan.is_selectable !== false))
        .map((plan) => this.buildSubscriptionOffer(plan, sectionId))
        .filter((plan) => plan.code && Number.isFinite(plan.price) && Number.isFinite(plan.tokens));

      mappedPlans.sort((left, right) => {
        const leftIndex = orderedTemplateCodes.indexOf(left.code);
        const rightIndex = orderedTemplateCodes.indexOf(right.code);
        if (leftIndex !== -1 || rightIndex !== -1) {
          if (leftIndex === -1) return 1;
          if (rightIndex === -1) return -1;
          return leftIndex - rightIndex;
        }
        return left.price - right.price;
      });

      return {
        ...template,
        plans: mappedPlans,
      };
    },
    async fetchAllSubscriptionPlans() {
      const session = await this.getSessionOrFail();
      const headers = {
        ...buildAuthHeaders(session, { includeContentType: false }),
        Accept: "application/json",
      };

      const response = await fetch(
        `${this.getApiUrl()}/momo/ecartpay/subscription-plans/all`,
        {
          method: "GET",
          headers,
        }
      );

      if (!response.ok) {
        let message = `HTTP ${response.status}`;
        try {
          const errorPayload = await response.json();
          if (typeof errorPayload?.detail === "string" && errorPayload.detail.trim()) {
            message = errorPayload.detail;
          }
        } catch (_error) {
          // no-op
        }
        throw new Error(message);
      }

      const body = await response.json();
      return {
        monthly: Array.isArray(body?.monthly?.plans) ? body.monthly.plans : [],
        yearly: Array.isArray(body?.yearly?.plans) ? body.yearly.plans : [],
      };
    },
    findFirstOfferForBillingCycle(cycle) {
      const section = (this.planSections || []).find((planSection) => planSection.id === cycle);
      if (!section || !Array.isArray(section.plans) || !section.plans.length) {
        return null;
      }
      return section.plans.find((plan) => plan.featured) || section.plans[0] || null;
    },
    ensureSelectedOfferCode() {
      if (this.selectedOfferCode && this.getOfferByCode(this.selectedOfferCode)) {
        return;
      }
      const nextOffer = this.findFirstOfferForBillingCycle(this.billingCycle)
        || this.findFirstOfferForBillingCycle("monthly")
        || this.catalogOffers[0]
        || null;
      this.selectedOfferCode = nextOffer?.code || "";
    },
    async loadSubscriptionCatalog() {
      if (!this.useDatabaseSubscriptionCatalog) {
        this.applyHardcodedSubscriptionCatalog();
        return;
      }

      const { monthly, yearly } = await this.fetchAllSubscriptionPlans();

      this.planSections = [
        this.buildSubscriptionSection("monthly", monthly),
        this.buildSubscriptionSection("annual", yearly),
      ];
      this.ensureSelectedOfferCode();
    },
    planOriginalPrice(plan) {
      const fromCatalog = Number(
        plan?.priceOriginal
        ?? plan?.price?.original
      );
      if (Number.isFinite(fromCatalog) && fromCatalog > 0) {
        return fromCatalog;
      }
      return null;
    },
    planListPriceAtBaseRate(plan) {
      return this.planOriginalPrice(plan);
    },
    planVolumeDiscountPercent(plan) {
      const originalPrice = this.planOriginalPrice(plan);
      const currentPrice = Number(plan?.price);
      if (!Number.isFinite(originalPrice) || originalPrice <= 0 || !Number.isFinite(currentPrice)) {
        return null;
      }
      if (currentPrice >= originalPrice - 0.0001) return null;
      const pct = Math.round(((originalPrice - currentPrice) / originalPrice) * 100);
      return pct > 0 ? pct : null;
    },
    applyBillingStatusSnapshot(snapshot) {
      if (!snapshot || typeof snapshot !== "object") return;
      this.billingStatus = snapshot;
      this.billingLoaded = true;
      useCurrentUserStore().applyBillingStatusSnapshot(snapshot);
    },
    async refreshBillingStatusFromStore() {
      try {
        const currentUserStore = useCurrentUserStore();
        const snapshot = await currentUserStore.syncCreditsFromBilling();
        if (snapshot && typeof snapshot === "object") {
          this.applyBillingStatusSnapshot(snapshot);
          return;
        }
        if (currentUserStore.billingStatus) {
          this.applyBillingStatusSnapshot(currentUserStore.billingStatus);
        }
      } catch (_error) {
        // no-op
      }
    },
    applySubscriptionStatusSnapshot(payload) {
      const normalizedPayload = this.normalizeSubscriptionPayload(payload);
      const previousOfferCode = this.currentSubscriptionOfferCode;

      if (normalizedPayload?.subscription_id) {
        this.subscriptionStatus = normalizedPayload;
      } else if (!this.pendingSubscription) {
        this.subscriptionStatus = null;
      }

      const nextOfferCode = this.currentSubscriptionOfferCode;
      if (!this.checkoutMode && nextOfferCode && nextOfferCode !== previousOfferCode) {
        this.selectedOfferCode = nextOfferCode;
        if (this.currentSubscriptionOffer) {
          this.syncBillingCycleFromOffer(this.currentSubscriptionOffer);
        }
      }

      const awaitingInitialSubscriptionCharge = !!normalizedPayload?.subscription_id
        && normalizedPayload.local_status === "active"
        && Number(normalizedPayload.successful_payments_count || 0) === 0
        && Number(normalizedPayload.granted_tokens_total || 0) === 0;
      const shouldTrackPendingSubscription = !!this.pendingSubscription
        || normalizedPayload?.local_status === "pending"
        || awaitingInitialSubscriptionCharge;
      if (shouldTrackPendingSubscription && normalizedPayload?.subscription_id) {
        this.pendingSubscription = {
          ...(this.pendingSubscription || {}),
          subscription_id: normalizedPayload.subscription_id,
          provider_subscription_id: normalizedPayload.provider_subscription_id || normalizedPayload.subscription_id,
          plan_code: normalizedPayload.plan_code || this.pendingSubscription?.plan_code || "",
          status: normalizedPayload.local_status || normalizedPayload.provider_status || this.pendingSubscription?.status || "",
        };
      } else if (!shouldTrackPendingSubscription) {
        this.pendingSubscription = null;
      }

      this.subscriptionLoaded = true;
      this.syncSubscriptionPolling();
      return normalizedPayload;
    },
    async readErrorMessage(response, fallbackMessage) {
      const responseText = await response.text();
      if (!responseText) return fallbackMessage;

      try {
        const payload = JSON.parse(responseText);
        if (typeof payload?.detail === "string") return payload.detail;
        if (typeof payload?.detail?.message === "string") return payload.detail.message;
        if (typeof payload?.message === "string") return payload.message;
        return JSON.stringify(payload.detail || payload);
      } catch (_error) {
        return responseText;
      }
    },
    openRequestErrorModal(message) {
      this.requestErrorMessage = message;
      this.showRequestErrorModal = true;
    },
    closeRequestErrorModal() {
      this.showRequestErrorModal = false;
      this.requestErrorMessage = "";
    },
    openSuccessModal(message) {
      this.successModalMessage = message;
      this.celebrationBurstKey += 1;
      this.showSuccessModal = true;
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      this.successModalMessage = "";
    },
    finishCheckoutSuccess(message) {
      this.loading = false;
      this.loadingActionKey = null;
      this.closeRequestErrorModal();
      this.cardFormError = "";
      this.resetCardForm();
      this.clearCheckoutSelection();
      this.clearCheckoutRoute();
      this.openSuccessModal(message);
    },
    finishCheckoutError(message) {
      this.loading = false;
      this.loadingActionKey = null;
      this.cardFormError = "";
      this.openRequestErrorModal(message);
    },
    resolveSubscriptionSubmissionResult(payload) {
      return payload?.new_subscription || payload;
    },
    resolveCheckoutConversionValue(offer) {
      const numeric = this.toFiniteNumber(offer?.price);
      return numeric == null ? undefined : numeric;
    },
    toFiniteNumber(value) {
      if (value === null || value === undefined || value === "") return null;
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    },
    extractSubscriptionCandidates(payload) {
      if (Array.isArray(payload)) {
        return payload.filter((item) => item && typeof item === "object");
      }
      if (!payload || typeof payload !== "object") return [];

      const listCandidates = [];
      const maybeArrays = [
        payload.subscriptions,
        payload.items,
        payload.results,
        payload.data,
      ];

      maybeArrays.forEach((entry) => {
        if (Array.isArray(entry)) {
          listCandidates.push(...entry.filter((item) => item && typeof item === "object"));
        }
      });

      if (payload.data && typeof payload.data === "object") {
        if (Array.isArray(payload.data.subscriptions)) {
          listCandidates.push(...payload.data.subscriptions.filter((item) => item && typeof item === "object"));
        }
        if (Array.isArray(payload.data.items)) {
          listCandidates.push(...payload.data.items.filter((item) => item && typeof item === "object"));
        }
      }

      return listCandidates;
    },
    toTimestamp(value) {
      if (!value) return 0;
      const parsed = Date.parse(value);
      return Number.isFinite(parsed) ? parsed : 0;
    },
    getSubscriptionStatusPriority(item) {
      const status = String(item?.local_status || "").toLowerCase();
      const priority = {
        active: 0,
        payment_failed: 1,
        pending: 2,
        canceled: 3,
        expired: 4,
      };
      if (Object.prototype.hasOwnProperty.call(priority, status)) {
        return priority[status];
      }
      return 10;
    },
    pickBestSubscription(items) {
      const subscriptions = (items || []).filter((item) => item && item.subscription_id);
      if (!subscriptions.length) return null;

      const sorted = subscriptions.slice().sort((a, b) => {
        const statusDiff = this.getSubscriptionStatusPriority(a) - this.getSubscriptionStatusPriority(b);
        if (statusDiff !== 0) return statusDiff;

        const updatedDiff = this.toTimestamp(b.updated_at) - this.toTimestamp(a.updated_at);
        if (updatedDiff !== 0) return updatedDiff;

        return this.toTimestamp(b.created_at) - this.toTimestamp(a.created_at);
      });

      return sorted[0];
    },
    normalizeSubscriptionPayload(payload) {
      const list = this.extractSubscriptionCandidates(payload);
      if (list.length) {
        return this.pickBestSubscription(list);
      }
      if (payload && typeof payload === "object" && payload.subscription_id) {
        return payload;
      }
      return null;
    },
    async getSessionOrFail() {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error || !session || !session.access_token) {
        throw new Error(this.tt("common.loginFirst", "Inicia sesión primero."));
      }
      return session;
    },
    async refreshSubscriptionStatus({ force = false } = {}) {
      this.subscriptionLoaded = false;
      try {
        const session = await this.getSessionOrFail();
        const currentUserStore = useCurrentUserStore();
        const payload = await currentUserStore.syncCommerceStatus(session, { force });
        if (payload?.billingStatus) {
          this.applyBillingStatusSnapshot(payload.billingStatus);
        }
        const normalizedPayload = this.applySubscriptionStatusSnapshot(payload?.subscriptionStatus);

        if (!normalizedPayload?.subscription_id && !this.pendingSubscription) {
          this.subscriptionStatus = null;
          this.stopSubscriptionPolling();
        }
      } catch (error) {
        this.subscriptionStatus = null;
      } finally {
        this.subscriptionLoaded = true;
      }
    },
    setBillingCycle(cycle) {
      if (this.billingCycle === cycle) return;
      this.billingCycle = cycle;
      const map = {
        unlimited_monthly: "unlimited_annual",
        unlimited_annual: "unlimited_monthly",
      };
      if (map[this.selectedOfferCode] && this.getOfferByCode(map[this.selectedOfferCode])) {
        this.selectedOfferCode = map[this.selectedOfferCode];
        return;
      }
      this.selectedOfferCode = this.findFirstOfferForBillingCycle(cycle)?.code || this.selectedOfferCode;
    },
    isSubscriptionPurchaseBlocked() {
      return this.hasActiveSubscriptionForPlanPurchase;
    },
    notifyActiveSubscriptionBlocksPlanPurchase() {
      const message = this.tt(
        "billing.activeSubscriptionBlocksPlanPurchase",
        "Ya tienes una suscripción activa. Administra tu plan desde el estado de suscripción."
      );
      this.$notify({
        message,
        type: "warning",
      });
    },
    openPaymentMethodUpdate() {
      if (!this.canUpdatePaymentMethod || this.loading) return;
      const offer = this.currentSubscriptionOffer || this.unlimitedOffer;
      if (!offer) return;
      this.paymentMethodUpdateMode = true;
      this.selectedOfferCode = offer.code;
      this.syncBillingCycleFromOffer(offer);
      this.selectedSubscription = offer;
      this.checkoutMode = true;
      this.cardFormError = "";
      this.showCardFieldValidation = false;
      this.clearPromo();
      this.resetCardForm();
      this.scrollToBillingTop();
    },
    choosePlan(plan) {
      if (!plan?.code) return;
      if (this.isSubscriptionPurchaseBlocked(plan)) {
        this.notifyActiveSubscriptionBlocksPlanPurchase();
        return;
      }
      this.openCheckoutRouteForOffer(plan.code);
    },
    getOfferByCode(code) {
      if (!code) return null;
      return this.catalogOffers.find((offer) => offer.code === code) || null;
    },
    syncBillingCycleFromOffer(offer) {
      if (!offer?.code) return;
      if (offer.periodType === "yearly") {
        this.billingCycle = "annual";
        return;
      }
      if (offer.periodType === "monthly") {
        this.billingCycle = "monthly";
        return;
      }
      if (offer.code.endsWith("_annual")) {
        this.billingCycle = "annual";
        return;
      }
      if (offer.code.endsWith("_monthly")) {
        this.billingCycle = "monthly";
      }
    },
    applyCheckoutSelection(offer, { resetForm = false } = {}) {
      if (!offer) return;
      if (this.isSubscriptionPurchaseBlocked(offer)) {
        this.notifyActiveSubscriptionBlocksPlanPurchase();
        return;
      }
      this.paymentMethodUpdateMode = false;
      this.selectedOfferCode = offer.code;
      this.syncBillingCycleFromOffer(offer);
      this.selectedSubscription = offer;
      this.checkoutMode = true;
      this.cardFormError = "";
      this.showCardFieldValidation = false;
      if (resetForm) {
        this.clearPromo();
      }
      if (resetForm) {
        this.resetCardForm();
      }
      if (resetForm) {
        this.trackCheckoutEvent("InitiateCheckout", offer);
      }
    },
    clearCheckoutSelection() {
      this.checkoutMode = false;
      this.selectedSubscription = null;
      this.paymentMethodUpdateMode = false;
      this.cardFormError = "";
      this.showCardFieldValidation = false;
      this.clearPromo();
    },
    applyCheckoutFromRoute() {
      const checkoutCode = this.embedded
        ? String(this.embeddedCheckoutCode || "").trim()
        : String(this.$route?.query?.checkout || "").trim();
      if (!checkoutCode) {
        this.clearCheckoutSelection();
        return;
      }

      const offer = this.getOfferByCode(checkoutCode);
      if (!offer) {
        this.clearCheckoutSelection();
        if (!this.embedded) {
          this.clearCheckoutRoute();
        } else {
          this.embeddedCheckoutCode = "";
        }
        return;
      }

      const shouldResetForm =
        !this.checkoutMode || this.selectedCheckoutOffer?.code !== offer.code;
      this.applyCheckoutSelection(offer, { resetForm: shouldResetForm });
    },
    openCheckoutRouteForOffer(offerCode) {
      const offer = this.getOfferByCode(offerCode);
      if (!offer) return;
      if (this.isSubscriptionPurchaseBlocked(offer)) {
        this.notifyActiveSubscriptionBlocksPlanPurchase();
        return;
      }

      if (this.embedded) {
        const currentCheckoutCode = String(this.embeddedCheckoutCode || "").trim();
        if (currentCheckoutCode === offer.code) {
          this.applyCheckoutSelection(offer, { resetForm: false });
          return;
        }
        this.embeddedCheckoutCode = offer.code;
        const shouldResetForm =
          !this.checkoutMode || this.selectedCheckoutOffer?.code !== offer.code;
        this.applyCheckoutSelection(offer, { resetForm: shouldResetForm });
        return;
      }

      const currentCheckoutCode = String(this.$route?.query?.checkout || "").trim();
      if (currentCheckoutCode === offer.code) {
        this.applyCheckoutSelection(offer, { resetForm: false });
        return;
      }

      this.$router.push({
        name: "billing",
        query: {
          ...(this.$route?.query || {}),
          checkout: offer.code,
        },
      });
    },
    clearCheckoutRoute() {
      if (this.embedded) {
        this.embeddedCheckoutCode = "";
        return;
      }
      const query = { ...(this.$route?.query || {}) };
      delete query.checkout;
      this.$router.replace({
        name: "billing",
        query,
      });
    },
    getSubscriptionPlanCode(offer) {
      if (!offer?.code) return "";
      return offer.backendPlanCode || offer.code;
    },
    trackCheckoutEvent(eventName, offer) {
      const userId = useCurrentUserStore().currentUserId;
      const planCode = this.getSubscriptionPlanCode(offer);
      if (!userId || !planCode) return;

      const eventPrefix = eventName === "InitiateCheckout" ? "ic" : "api";
      trackMetaStandardEvent(
        eventName,
        {
          content_name: planCode,
          value: this.checkoutPriceAmount,
          currency: "USD",
        },
        { eventID: `${eventPrefix}-${userId}-${planCode}` }
      );
    },
    isCurrentSubscriptionPlan(plan) {
      return !!plan?.code && this.currentSubscriptionOfferCode === plan.code && this.currentSubscriptionStatus !== "canceled";
    },
    isPlanSelectionDisabled(plan) {
      return this.loading || this.isCurrentSubscriptionPlan(plan);
    },
    planCtaLabel(plan) {
      if (this.isCurrentSubscriptionPlan(plan)) {
        return this.tt("billing.manageSubscription", "Gestiona tu suscripción");
      }
      if (this.canUpgradeSubscription) {
        return this.tt("billing.changePlan", "Cambiar plan");
      }
      return this.tt("billing.startSubscription", "Iniciar suscripción");
    },
    openSubscriptionManagement() {
      this.$router.push("/user-profile");
    },
    closeCheckout() {
      if (this.loading) return;
      if (this.paymentMethodUpdateMode) {
        this.clearCheckoutSelection();
        return;
      }
      this.clearCheckoutRoute();
    },
    resetCardForm() {
      const country = this.cardForm.country || "";
      this.cardForm = {
        country,
        phone: "",
        card_name: "",
        card_number: "",
        exp_mmyy: "",
        cvc: "",
      };
      this.showCardFieldValidation = false;
      this.syncCountrySearchFromCode(country);
      this.showCountryMenu = false;
    },
    keepDigits(value, maxLen) {
      return String(value || "")
        .replace(/\D/g, "")
        .slice(0, maxLen);
    },
    detectCardBrand(value) {
      const digits = String(value || "").replace(/\D/g, "");
      if (!digits) return "";
      if (/^3[47]/.test(digits)) return "amex";
      if (/^4/.test(digits)) return "visa";

      const prefixTwo = Number(digits.slice(0, 2));
      const prefixFour = Number(digits.slice(0, 4));
      if (
        (prefixTwo >= 51 && prefixTwo <= 55)
        || (prefixFour >= 2221 && prefixFour <= 2720)
      ) {
        return "mastercard";
      }

      return "";
    },
    onCardNumberInput() {
      const digits = String(this.cardForm.card_number || "").replace(/\D/g, "");
      const brand = this.detectCardBrand(digits);
      const maxLen = brand === "amex" ? 15 : 16;
      this.cardForm.card_number = digits.slice(0, maxLen);
    },
    onPhoneInput() {
      this.cardForm.phone = normalizeInternationalPhoneInput(this.cardForm.phone);
    },
    onExpMmyyInput() {
      const digits = this.keepDigits(this.cardForm.exp_mmyy, 4);
      if (digits.length <= 2) {
        this.cardForm.exp_mmyy = digits;
        return;
      }
      let month = Number(digits.slice(0, 2));
      if (month <= 0) month = 1;
      if (month > 12) month = 12;
      const mm = String(month).padStart(2, "0");
      const yy = digits.slice(2, 4);
      this.cardForm.exp_mmyy = `${mm}/${yy}`;
    },
    onCvcInput() {
      this.cardForm.cvc = this.keepDigits(this.cardForm.cvc, this.cardCvcMaxLength);
    },
    formatLocalDate(isoStr) {
      if (!isoStr) return "";
      try {
        return new Date(isoStr).toLocaleDateString(
          this.isSpanish ? "es" : "en",
          { year: "numeric", month: "long", day: "numeric" }
        );
      } catch (_e) {
        return String(isoStr).slice(0, 10);
      }
    },
    getCardRules(cardNumber) {
      const digits = String(cardNumber || "").replace(/\D/g, "");
      const isAmex = /^3[47]/.test(digits);
      return {
        expectedCardLength: isAmex ? 15 : 16,
        expectedCvcLength: isAmex ? 4 : 3,
      };
    },
    getCountryLabel(code) {
      const normalizedCode = String(code || "").trim().toUpperCase();
      if (!normalizedCode) return "";
      const match = this.countryOptions.find((country) => country.code === normalizedCode);
      return match ? `${match.name} (${match.code})` : normalizedCode;
    },
    syncCountrySearchFromCode(code) {
      this.countrySearch = this.getCountryLabel(code);
    },
    openCountryMenu() {
      if (this.loading) return;
      this.showCountryMenu = true;
    },
    onCountrySearchInput() {
      this.showCountryMenu = true;
      this.cardForm.country = "";
      const query = String(this.countrySearch || "").trim().toLowerCase();
      if (!query) return;
      const exactMatch = this.countryOptions.find(
        (country) => country.name.trim().toLowerCase() === query
      );
      if (exactMatch) this.selectCountry(exactMatch);
    },
    clearSelectedCountry() {
      this.cardForm.country = "";
      this.countrySearch = "";
      this.showCountryMenu = true;
      this.$nextTick(() => {
        if (this.$refs.countrySearchInput) this.$refs.countrySearchInput.focus();
      });
    },
    onCountrySearchBlur() {
      window.setTimeout(() => {
        this.showCountryMenu = false;
        if (!this.cardForm.country) {
          this.countrySearch = "";
          return;
        }
        this.syncCountrySearchFromCode(this.cardForm.country);
      }, 100);
    },
    selectCountry(country) {
      this.cardForm.country = country.code;
      this.syncCountrySearchFromCode(country.code);
      this.showCountryMenu = false;
    },
    parseExpMmyy() {
      const raw = (this.cardForm.exp_mmyy || "").replace(/\D/g, "");
      if (raw.length !== 4) return { month: null, year: null };
      const month = Number(raw.slice(0, 2));
      const yy = Number(raw.slice(2, 4));
      const year = 2000 + yy;
      return { month, year };
    },
    normalizeBillingPhone(value) {
      return normalizeInternationalPhoneInput(value);
    },
    getCardFieldError(field) {
      const country = String(this.cardForm.country || "").trim().toUpperCase();
      const normalizedPhone = this.normalizeBillingPhone(this.cardForm.phone || "");
      const cardName = (this.cardForm.card_name || "").trim();
      const cardNumber = (this.cardForm.card_number || "").replace(/\D/g, "");
      const rules = this.getCardRules(cardNumber);
      const { month: expMonth, year: expYear } = this.parseExpMmyy();
      const cvc = (this.cardForm.cvc || "").replace(/\D/g, "");

      if (field === "country" && country.length !== 2) {
        return this.tt("billing.countryRequired", "El país es obligatorio.");
      }
      if (field === "phone" && !normalizedPhone) {
        return this.tt("billing.phoneRequired", "El teléfono es obligatorio.");
      }
      if (field === "phone" && !isInternationalPhone(normalizedPhone)) {
        return this.tt(
          "billing.phoneFormatInvalid",
          "Usa el formato +525512345678."
        );
      }
      if (field === "card_name" && cardName.length < 2) {
        return this.tt("billing.nameOnCardRequired", "El nombre en la tarjeta es obligatorio.");
      }
      if (field === "card_number" && cardNumber.length !== rules.expectedCardLength) {
        return this.tt("billing.cardNumber16", "El número debe tener 15 dígitos para Amex o 16 para Visa y Mastercard.");
      }
      if (field === "exp_mmyy" && (expMonth == null || expMonth < 1 || expMonth > 12)) {
        return this.tt("billing.expiryMonthInvalid", "El mes de expiración debe ser 01-12.");
      }
      if (field === "exp_mmyy" && (expYear == null || expYear < new Date().getUTCFullYear())) {
        return this.tt("billing.expiryYearInvalid", "El año de expiración no es válido o está vencido.");
      }
      if (field === "cvc" && cvc.length !== rules.expectedCvcLength) {
        return this.$i18n?.locale === "en"
          ? `CVC must be ${rules.expectedCvcLength} digits.`
          : `El CVC debe tener ${rules.expectedCvcLength} dígitos.`;
      }
      return "";
    },
    getVisibleCardFieldError(field) {
      if (!this.showCardFieldValidation) return "";
      return this.getCardFieldError(field);
    },
    isCardFieldInvalid(field) {
      return this.showCardFieldValidation && !!this.getCardFieldError(field);
    },
    hasVisibleCardFieldErrors() {
      if (!this.showCardFieldValidation) return false;
      return CARD_FORM_FIELDS.some((field) => !!this.getCardFieldError(field));
    },
    validateCardForm() {
      for (const field of CARD_FORM_FIELDS) {
        const error = this.getCardFieldError(field);
        if (error) return error;
      }
      return "";
    },
    selectedCheckoutPromoOfferCode() {
      if (!this.selectedCheckoutOffer) return "";
      return this.getSubscriptionPlanCode(this.selectedSubscription);
    },
    onPromoInput() {
      this.promoCode = String(this.promoCode || "").toUpperCase();
      if (this.promoStatus !== "idle") {
        this.promoStatus = "idle";
        this.promoMessage = "";
        this.appliedPromoCode = "";
        this.appliedPromoDiscountPercent = 0;
        this.appliedPromoBonusTokens = 0;
      }
    },
    clearPromo() {
      this.promoCode = "";
      this.appliedPromoCode = "";
      this.appliedPromoDiscountPercent = 0;
      this.appliedPromoBonusTokens = 0;
      this.promoStatus = "idle";
      this.promoMessage = "";
    },
    async applyPromoCode() {
      const code = String(this.promoCode || "").trim().toUpperCase();
      const offerCode = this.selectedCheckoutPromoOfferCode();
      if (!code || !offerCode || this.promoStatus === "checking") return;

      this.promoStatus = "checking";
      this.promoMessage = "";
      try {
        const session = await this.getSessionOrFail();
        const response = await fetch(`${this.getApiUrl()}/momo/ecartpay/promo/validate`, {
          method: "POST",
          headers: buildAuthHeaders(session),
          body: JSON.stringify({
            code,
            offer_code: offerCode,
          }),
        });
        if (!response.ok) {
          throw new Error(
            await this.readErrorMessage(
              response,
              this.tt("billing.promoInvalid", "El código promocional no es válido.")
            )
          );
        }
        const result = await response.json();
        if (result && result.valid) {
          const discountPercent = Number(result.discount_percent || 0);
          const bonusTokens = Number(result.bonus_tokens || 0);
          this.appliedPromoCode = String(result.code || code);
          this.appliedPromoDiscountPercent = Number.isFinite(discountPercent) ? discountPercent : 0;
          this.appliedPromoBonusTokens = Number.isFinite(bonusTokens) ? bonusTokens : 0;
          this.promoCode = this.appliedPromoCode;
          this.promoStatus = "valid";
          this.promoMessage = this.appliedPromoDiscountPercent > 0
            ? this.tt("billing.promoApplied", "{percent}% de descuento aplicado a tu plan.")
              .replace("{percent}", this.appliedPromoDiscountPercent)
            : result.message;
        } else {
          this.appliedPromoCode = "";
          this.appliedPromoDiscountPercent = 0;
          this.appliedPromoBonusTokens = 0;
          this.promoStatus = "invalid";
          this.promoMessage = (result && result.message)
            || this.tt("billing.promoInvalid", "El código promocional no es válido.");
        }
      } catch (error) {
        this.appliedPromoCode = "";
        this.appliedPromoDiscountPercent = 0;
        this.appliedPromoBonusTokens = 0;
        this.promoStatus = "invalid";
        this.promoMessage = error?.message || this.tt("billing.promoInvalid", "El código promocional no es válido.");
      }
    },
    async submitCheckout() {
      if (!this.isSubscriptionCheckout) return;
      if (this.paymentMethodUpdateMode) {
        await this.submitPaymentMethodUpdate();
        return;
      }
      if (this.isSubscriptionPurchaseBlocked()) {
        this.notifyActiveSubscriptionBlocksPlanPurchase();
        this.closeCheckout();
        return;
      }
      if (this.isUpgradeCheckout) {
        await this.submitSubscriptionUpgrade();
        return;
      }
      await this.submitSubscriptionStart();
    },
    async submitPaymentMethodUpdate() {
      if (!this.canUpdatePaymentMethod) return;
      this.showCardFieldValidation = true;
      this.cardFormError = this.validateCardForm();
      if (this.cardFormError) return;

      this.loading = true;
      this.loadingActionKey = "subscription:update_payment_method";
      let shouldReleaseLoading = true;
      try {
        const session = await this.getSessionOrFail();
        const { month, year } = this.parseExpMmyy();
        const response = await fetch(`${this.getApiUrl()}/momo/ecartpay/subscriptions/update-payment-method`, {
          method: "POST",
          headers: buildAuthHeaders(session),
          body: JSON.stringify({
            country: String(this.cardForm.country || "").trim().toUpperCase(),
            phone: this.normalizeBillingPhone(this.cardForm.phone),
            card_name: (this.cardForm.card_name || "").trim(),
            card_number: (this.cardForm.card_number || "").replace(/\D/g, ""),
            exp_month: month,
            exp_year: year,
            cvc: (this.cardForm.cvc || "").replace(/\D/g, ""),
            subscription_id: this.subscriptionStatus?.subscription_id || null,
            provider_subscription_id:
              this.subscriptionStatus?.provider_subscription_id
              || this.pendingSubscription?.provider_subscription_id
              || null,
          }),
        });
        if (!response.ok) {
          throw new Error(
            await this.readErrorMessage(
              response,
              this.tt("billing.unableToUpdatePaymentMethod", "No se pudo actualizar tu método de pago.")
            )
          );
        }

        const payload = await response.json();
        shouldReleaseLoading = false;
        await this.handleSubscriptionSubmissionSuccess(payload, {
          fallbackPlanCode: this.currentSubscriptionOfferCode,
        });
      } catch (error) {
        this.finishCheckoutError(error?.message || this.tt("common.unexpectedError", "Error inesperado"));
      } finally {
        if (shouldReleaseLoading) {
          this.loading = false;
          this.loadingActionKey = null;
        }
      }
    },
    async submitSubscriptionStart() {
      if (!this.selectedSubscription) return;
      this.showCardFieldValidation = true;
      this.cardFormError = this.validateCardForm();
      if (this.cardFormError) return;

      const planCode = this.getSubscriptionPlanCode(this.selectedSubscription);
      if (!planCode) {
        this.cardFormError = this.tt("billing.missingSubscriptionPlanCode", "No se pudo resolver el plan de suscripción.");
        return;
      }

      const conversionValue = this.checkoutPriceAmount;
      this.trackCheckoutEvent("AddPaymentInfo", this.selectedSubscription);
      this.loading = true;
      this.loadingActionKey = "subscription:submit";
      let shouldReleaseLoading = true;
      try {
        const session = await this.getSessionOrFail();
        const { month, year } = this.parseExpMmyy();
        const response = await fetch(`${this.getApiUrl()}/momo/ecartpay/subscriptions/start`, {
          method: "POST",
          headers: buildAuthHeaders(session),
          body: JSON.stringify({
            plan_code: planCode,
            country: String(this.cardForm.country || "").trim().toUpperCase(),
            phone: this.normalizeBillingPhone(this.cardForm.phone),
            payment_method: "card",
            card_name: (this.cardForm.card_name || "").trim(),
            card_number: (this.cardForm.card_number || "").replace(/\D/g, ""),
            exp_month: month,
            exp_year: year,
            cvc: (this.cardForm.cvc || "").replace(/\D/g, ""),
            promo_code: this.promoStatus === "valid" ? this.appliedPromoCode : "",
            // Meta attribution (_fbp/_fbc cookies + current URL) so the CAPI
            // Subscribe fired by the payment webhook can credit the campaign.
            ...buildSignupAttribution(),
          }),
        });
        if (!response.ok) {
          throw new Error(
            await this.readErrorMessage(
              response,
              this.tt("billing.unableToStartSubscription", "No se pudo iniciar la suscripción.")
            )
          );
        }

        const payload = await response.json();
        shouldReleaseLoading = false;
        await this.handleSubscriptionSubmissionSuccess(payload, {
          fallbackPlanCode: planCode,
          conversionValue,
        });
      } catch (error) {
        this.finishCheckoutError(error?.message || this.tt("common.unexpectedError", "Error inesperado"));
      } finally {
        if (shouldReleaseLoading) {
          this.loading = false;
          this.loadingActionKey = null;
        }
      }
    },
    async submitSubscriptionUpgrade() {
      if (!this.selectedSubscription || !this.isUpgradeCheckout) return;
      this.showCardFieldValidation = true;
      this.cardFormError = this.validateCardForm();
      if (this.cardFormError) return;

      const planCode = this.getSubscriptionPlanCode(this.selectedSubscription);
      if (!planCode) {
        this.cardFormError = this.tt("billing.missingSubscriptionPlanCode", "No se pudo resolver el plan de suscripción.");
        return;
      }

      const conversionValue = this.checkoutPriceAmount;
      this.trackCheckoutEvent("AddPaymentInfo", this.selectedSubscription);
      this.loading = true;
      this.loadingActionKey = "subscription:upgrade";
      let shouldReleaseLoading = true;
      try {
        const session = await this.getSessionOrFail();
        const { month, year } = this.parseExpMmyy();
        const response = await fetch(`${this.getApiUrl()}/momo/ecartpay/subscriptions/upgrade`, {
          method: "POST",
          headers: buildAuthHeaders(session),
          body: JSON.stringify({
            plan_code: planCode,
            country: String(this.cardForm.country || "").trim().toUpperCase(),
            phone: this.normalizeBillingPhone(this.cardForm.phone),
            payment_method: "card",
            card_name: (this.cardForm.card_name || "").trim(),
            card_number: (this.cardForm.card_number || "").replace(/\D/g, ""),
            exp_month: month,
            exp_year: year,
            cvc: (this.cardForm.cvc || "").replace(/\D/g, ""),
            current_subscription_id: this.subscriptionStatus?.subscription_id || null,
            current_provider_subscription_id:
              this.subscriptionStatus?.provider_subscription_id
              || this.pendingSubscription?.provider_subscription_id
              || null,
            promo_code: this.promoStatus === "valid" ? this.appliedPromoCode : "",
          }),
        });
        if (!response.ok) {
          throw new Error(
            await this.readErrorMessage(
              response,
              this.tt("billing.unableToUpgradeSubscription", "No se pudo actualizar la suscripción.")
            )
          );
        }

        const payload = await response.json();
        shouldReleaseLoading = false;
        await this.handleSubscriptionSubmissionSuccess(payload, {
          fallbackPlanCode: planCode,
          conversionValue,
        });
      } catch (error) {
        this.finishCheckoutError(error?.message || this.tt("common.unexpectedError", "Error inesperado"));
      } finally {
        if (shouldReleaseLoading) {
          this.loading = false;
          this.loadingActionKey = null;
        }
      }
    },
    async handleSubscriptionSubmissionSuccess(payload, options = {}) {
      const subscriptionResult = this.resolveSubscriptionSubmissionResult(payload);
      const fallbackPlanCode = options.fallbackPlanCode || "";
      const conversionValue = options.conversionValue;
      this.shouldPollSubscriptionStatus = true;
      this.pendingSubscription = {
        reference_id: subscriptionResult.reference_id,
        provider_subscription_id: subscriptionResult.provider_subscription_id || null,
        plan_code: subscriptionResult.plan_code || fallbackPlanCode,
        status: subscriptionResult.status || "pending",
        conversion_value: conversionValue,
      };
      await this.checkPendingSubscriptionNow();
      this.syncSubscriptionPolling();
    },
    clearPendingPurchase() {
      this.pendingPurchase = null;
    },
    clearPendingSubscription() {
      this.shouldPollSubscriptionStatus = false;
      this.pendingSubscription = null;
    },
    restoreCancellationResult() {
      try {
        const raw = localStorage.getItem(CANCELLATION_INFO_STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return;
        this.cancellationResult = {
          tokens_usable_until: parsed.tokens_usable_until || null,
          next_payment_date: parsed.next_payment_date || null,
          strict_expiry_lots_count: parsed.strict_expiry_lots_count ?? null,
        };
      } catch (_error) {
        // no-op
      }
    },
    persistCancellationResult() {
      try {
        if (!this.cancellationResult) {
          localStorage.removeItem(CANCELLATION_INFO_STORAGE_KEY);
          return;
        }
        localStorage.setItem(
          CANCELLATION_INFO_STORAGE_KEY,
          JSON.stringify(this.cancellationResult)
        );
      } catch (_error) {
        // no-op
      }
    },
    clearCancellationResult() {
      this.cancellationResult = null;
      this.persistCancellationResult();
    },
    restorePendingPurchase() {
      if (this.embedded) return;
      const fromQueryReference = this.$route?.query?.reference_id;
      if (fromQueryReference && typeof fromQueryReference === "string") {
        this.pendingPurchase = {
          reference_id: fromQueryReference,
          status: "pending",
        };
      }
    },
    async checkPendingPurchaseNow() {
      if (!this.pendingPurchase?.reference_id) return;
      try {
        const session = await this.getSessionOrFail();
        const response = await fetch(
          `${this.getApiUrl()}/momo/ecartpay/purchase-status?reference_id=${encodeURIComponent(this.pendingPurchase.reference_id)}`,
          {
            method: "GET",
            headers: buildAuthHeaders(session, { includeContentType: false }),
          }
        );
        if (!response.ok) return;
        const payload = await response.json();
        if (payload?.billing_status) {
          this.applyBillingStatusSnapshot(payload.billing_status);
        }
        if (payload?.subscription_status) {
          this.applySubscriptionStatusSnapshot(payload.subscription_status);
        }
        this.pendingPurchase = {
          ...this.pendingPurchase,
          ...payload,
        };

        if (payload.status === "confirmed") {
          this.stopPendingPurchasePolling();
          this.clearPendingPurchase();
          await useCurrentUserStore().refresh(null, { syncCommerce: true });
          this.finishCheckoutSuccess(
            this.tt("billing.paymentConfirmed", "Tu pago fue confirmado correctamente.")
          );
        } else if (payload.status === "failed") {
          this.stopPendingPurchasePolling();
          this.clearPendingPurchase();
          this.finishCheckoutError(
            payload.error_message || this.tt("billing.purchaseFailed", "No se pudo completar tu pago.")
          );
        }
      } catch (error) {
        // no-op
      }
    },
    async checkPendingSubscriptionNow() {
      const hadPendingContext = !!this.pendingSubscription || this.isSubscriptionPending;
      if (!hadPendingContext) return;
      await this.refreshSubscriptionStatus({ force: true });

      const matchesPendingPlan = !this.pendingSubscription?.plan_code
        || this.subscriptionStatus?.plan_code === this.pendingSubscription.plan_code;
      const hasConfirmedSubscriptionPayment = Number(this.subscriptionStatus?.successful_payments_count || 0) > 0
        || Number(this.subscriptionStatus?.granted_tokens_total || 0) > 0;
      const subscriptionActive = this.currentSubscriptionStatus === "active";

      if (
        hadPendingContext
        && matchesPendingPlan
        && subscriptionActive
        && hasConfirmedSubscriptionPayment
      ) {
        this.stopSubscriptionPolling();
        this.clearPendingSubscription();
        await useCurrentUserStore().refresh(null, { syncCommerce: true });
        let successNotice = this.tt("billing.subscriptionActiveNotice", "Tu suscripción ya está activa.");
        if (this.loadingActionKey === "subscription:upgrade") {
          successNotice = this.tt("billing.subscriptionUpgradeConfirmed", "Tu cambio de plan fue confirmado correctamente.");
        } else if (this.loadingActionKey === "subscription:update_payment_method") {
          successNotice = this.tt("billing.paymentMethodUpdateConfirmed", "Tu método de pago se actualizó y el cobro fue confirmado.");
        }
        this.finishCheckoutSuccess(successNotice);
      } else if (hadPendingContext && ["payment_failed", "paused", "canceled"].includes(this.currentSubscriptionStatus)) {
        this.stopSubscriptionPolling();
        this.clearPendingSubscription();
        const updatePaymentMethodFailedMessage = this.loadingActionKey === "subscription:update_payment_method"
          ? this.tt("billing.paymentMethodUpdateFailed", "Actualizamos tu tarjeta, pero el cobro todavía no pudo confirmarse. Revisa los datos o intenta con otra tarjeta.")
          : null;
        this.finishCheckoutError(
          this.subscriptionStatus?.latest_error_message
          || this.subscriptionIssueBannerText
          || updatePaymentMethodFailedMessage
          || (
            this.loadingActionKey === "subscription:upgrade"
              ? this.tt("billing.subscriptionUpgradeFailed", "No se pudo confirmar tu cambio de plan.")
              : this.tt("billing.subscriptionStartFailed", "No se pudo confirmar tu suscripción.")
          )
        );
      }
    },
    startPendingPurchasePolling() {
      this.stopPendingPurchasePolling();
      if (!this.pendingPurchase?.reference_id) return;
      this.pendingPurchasePoller = setInterval(() => {
        this.checkPendingPurchaseNow();
      }, PURCHASE_STATUS_POLL_INTERVAL_MS);
    },
    startSubscriptionPolling() {
      this.stopSubscriptionPolling();
      if (!this.shouldPollSubscriptionStatus) return;
      if (!this.pendingSubscription && !this.isSubscriptionPending) return;
      this.subscriptionPoller = setInterval(() => {
        this.checkPendingSubscriptionNow();
      }, SUBSCRIPTION_STATUS_POLL_INTERVAL_MS);
    },
    stopPendingPurchasePolling() {
      if (this.pendingPurchasePoller) {
        clearInterval(this.pendingPurchasePoller);
        this.pendingPurchasePoller = null;
      }
    },
    stopSubscriptionPolling() {
      if (this.subscriptionPoller) {
        clearInterval(this.subscriptionPoller);
        this.subscriptionPoller = null;
      }
    },
    syncSubscriptionPolling() {
      if (!this.shouldPollSubscriptionStatus) {
        this.stopSubscriptionPolling();
        return;
      }
      if (this.pendingSubscription || this.isSubscriptionPending) {
        this.startSubscriptionPolling();
        return;
      }
      this.stopSubscriptionPolling();
    },
    openCancellationModal() {
      this.cancellationReasonType = "";
      this.cancellationMessage = "";
      this.cancellationFormError = "";
      this.showCancellationModal = true;
    },
    closeCancellationModal() {
      if (this.loading) return;
      this.showCancellationModal = false;
      this.cancellationFormError = "";
    },
    async cancelSubscription() {
      if (!this.canCancelSubscription || this.loading) return;
      if (!this.cancellationReasonType || !this.cancellationMessage.trim()) {
        this.cancellationFormError = this.tt(
          "billing.cancelReasonRequired",
          "Selecciona un motivo y escribe un mensaje para continuar."
        );
        return;
      }
      this.loading = true;
      this.loadingActionKey = "subscription:cancel";
      try {
        const session = await this.getSessionOrFail();
        const cancellationPayload = {
          reason: `${this.cancellationReasonType}: ${this.cancellationMessage.trim()}`,
        };
        const localSubscriptionId = this.subscriptionStatus?.subscription_id;
        const providerSubscriptionId =
          this.subscriptionStatus?.provider_subscription_id
          || this.pendingSubscription?.provider_subscription_id;

        if (localSubscriptionId) {
          cancellationPayload.subscription_id = String(localSubscriptionId);
        }
        if (providerSubscriptionId) {
          cancellationPayload.provider_subscription_id = String(providerSubscriptionId);
        }

        await createCancellationFeedback(session, {
          reasonType: this.cancellationReasonType,
          message: this.cancellationMessage.trim(),
        });

        const response = await fetch(`${this.getApiUrl()}/momo/ecartpay/subscriptions/cancel`, {
          method: "POST",
          headers: buildAuthHeaders(session),
          body: JSON.stringify(cancellationPayload),
        });
        if (!response.ok) {
          throw new Error(
            await this.readErrorMessage(
              response,
              this.tt("billing.unableToCancelSubscription", "No se pudo cancelar la suscripción.")
            )
          );
        }
        const cancelPayload = await response.json();
        this.cancellationResult = {
          tokens_usable_until: cancelPayload.tokens_usable_until || null,
          next_payment_date: cancelPayload.next_payment_date || null,
          benefits_end_at: cancelPayload.benefits_end_at || null,
          cancel_at_period_end: !!cancelPayload.cancel_at_period_end,
          strict_expiry_lots_count: cancelPayload.strict_expiry_lots_count ?? null,
        };
        this.persistCancellationResult();
        this.$notify({
          message: cancelPayload?.message || this.tt("billing.subscriptionCancelledNotice", "Suscripción cancelada correctamente."),
          type: "success",
        });
        await this.refreshSubscriptionStatus({ force: true });
        this.showCancellationModal = false;
      } catch (error) {
        this.openRequestErrorModal(error?.message || this.tt("common.unexpectedError", "Error inesperado"));
      } finally {
        this.loading = false;
        this.loadingActionKey = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/sass/clipr/confirm-modal";

/* Integrado con DashboardLayout y TopNavbar - criterio SchedulePost / VODClips */
.pricing-page {
  position: relative;
  background: transparent;
  color: var(--clipr-text-primary);
  padding: 0 0 20px;
  --pricing-section-gap: 20px;
  --pricing-embedded-horizontal-inset: 4px;
  --pricing-embedded-vertical-inset: 8px;
}

.pricing-page--embedded {
  min-height: auto;
  padding-bottom: 0;
}

.pricing-inner {
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: var(--pricing-section-gap);
}

.pricing-page--embedded .pricing-inner {
  max-width: none;
  padding: 0 var(--pricing-embedded-horizontal-inset) var(--pricing-embedded-vertical-inset);
}

.pricing-page :deep(.text-muted) {
  color: var(--clipr-text-secondary) !important;
}

.pricing-page :deep(.btn-primary) {
  background: var(--clipr-purple-gradient);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 8px 30px var(--clipr-purple-glow);
}

.pricing-page :deep(.btn-primary:hover),
.pricing-page :deep(.btn-primary:focus) {
  border-color: transparent;
  color: #fff;
  box-shadow: 0 12px 40px rgba(168, 85, 247, 0.35);
}

.pricing-page :deep(.btn-outline-primary) {
  background: rgba(255, 255, 255, 0.03);
  border-color: var(--clipr-border-color);
  color: var(--clipr-text-primary);
}

.pricing-page :deep(.btn-outline-primary:hover),
.pricing-page :deep(.btn-outline-primary:focus) {
  background: rgba(168, 85, 247, 0.12);
  border-color: var(--clipr-border-purple);
  color: var(--clipr-purple-light);
}

.pricing-page-heading {
  margin-bottom: 4px;
}

.pricing-page-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 0 8px;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--clipr-text-primary);
}

.pricing-page-title-icon {
  font-size: 1.35rem;
  color: var(--clipr-purple-light);
}

.pricing-page-lead {
  margin: 0;
  font-size: 0.9375rem;
  color: var(--clipr-text-secondary);
  max-width: 520px;
}

.pricing-section {
  background: #746ac712;
  border-radius: var(--clipr-radius-lg);
  padding: 18px 20px;
  border: 1px solid var(--clipr-border-color);
  box-shadow: none;
}

.pricing-status-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--pricing-section-gap);
  align-items: stretch;
}

.pricing-status-card {
  height: 100%;
}

.pricing-balance-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background:
    linear-gradient(145deg, rgba(26, 22, 37, 0.92) 0%, rgba(35, 28, 52, 0.78) 100%);
  border-color: rgba(168, 85, 247, 0.18);
  padding: 22px 24px;
}

.pricing-balance-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}

.pricing-balance-copy {
  flex: 1 1 auto;
  min-width: 0;
}

.pricing-balance-label {
  margin: 0 0 4px;
  font-size: 0.8125rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--clipr-text-secondary);
}

.pricing-balance-title {
  margin: 0 0 12px;
  font-size: clamp(1.5rem, 3vw, 1.875rem);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: var(--clipr-text-primary);
}

.pricing-balance-title--muted {
  color: var(--clipr-text-secondary);
  font-size: 1.25rem;
  font-weight: 600;
}

.pricing-balance-tokens {
  color: var(--clipr-purple-light);
  font-weight: 700;
}

.pricing-balance-meta-row {
  margin-top: 2px;
}

.pricing-balance-hours-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  border: 1px solid rgba(168, 85, 247, 0.45);
  background: rgba(168, 85, 247, 0.14);
  color: var(--clipr-purple-light);
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1;
  padding: 6px 12px;
}

.pricing-balance-hint {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 10px 0 0;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.45;
  color: var(--clipr-text-tertiary);
}

.pricing-balance-hint__rule {
  color: var(--clipr-text-secondary);
  font-weight: 600;
}

.pricing-balance-hint__example {
  font-size: 0.8125rem;
  color: var(--clipr-text-tertiary);
}

.pricing-selection-notice {
  border-color: rgba(168, 85, 247, 0.35);
  background: rgba(168, 85, 247, 0.1);
}

.pricing-subscription-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background:
    linear-gradient(145deg, rgba(26, 22, 37, 0.92) 0%, rgba(35, 28, 52, 0.78) 100%);
  border-color: rgba(168, 85, 247, 0.18);
  padding: 22px 24px;
}

.pricing-subscription-section--active {
  border-color: rgba(34, 197, 94, 0.18);
}

.pricing-subscription-section--cancelled {
  border-color: rgba(245, 158, 11, 0.24);
  box-shadow: inset 0 1px 0 rgba(245, 158, 11, 0.06);
}

.pricing-subscription-section--empty {
  border-color: rgba(168, 85, 247, 0.12);
}

.pricing-subscription-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.pricing-subscription-copy {
  flex: 1 1 auto;
  min-width: 0;
}

.pricing-subscription-title {
  margin: 0 0 12px;
  font-size: clamp(1.5rem, 3vw, 1.875rem);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: var(--clipr-text-primary);
}

.pricing-subscription-meta {
  color: var(--clipr-text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
}

.pricing-subscription-status-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
}

.pricing-subscription-status-text {
  color: var(--clipr-text-secondary);
  font-size: 0.9375rem;
  line-height: 1.4;
}

.pricing-subscription-status-text strong {
  color: var(--clipr-text-primary);
  font-weight: 700;
}

.pricing-subscription-lifecycle {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 2px;
  max-width: 28rem;
}

.pricing-subscription-lifecycle__badge {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  border-radius: 999px;
  border: 1px solid rgba(245, 158, 11, 0.42);
  background: rgba(245, 158, 11, 0.12);
  color: #fbbf24;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1;
  padding: 5px 11px;
  text-transform: uppercase;
}

.pricing-subscription-lifecycle__panel {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.pricing-subscription-lifecycle__access {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--clipr-text-primary);
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.4;
}

.pricing-subscription-lifecycle__live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: #34d399;
  box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.2);
}

.pricing-subscription-lifecycle__hint {
  padding-left: 18px;
  color: var(--clipr-text-tertiary);
  font-size: 0.8125rem;
  line-height: 1.45;
}

.pricing-subscription-end-date {
  margin-top: 10px;
  color: var(--clipr-text-tertiary);
  font-size: 0.8125rem;
  line-height: 1.45;
}

.pricing-subscription-empty-copy {
  margin-top: 4px;
  color: var(--clipr-text-secondary);
  font-size: 0.875rem;
  line-height: 1.55;
  max-width: 36rem;
}

.pricing-subscription-status-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex-shrink: 0;
  background: #22c55e;
  box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.22);
}

.pricing-subscription-section--active .pricing-subscription-status-dot {
  background: #34d399;
  box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.2);
}

.pricing-subscription-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 10px;
  flex: 0 0 auto;
  width: min(100%, 260px);
  margin-left: auto;
}

.pricing-subscription-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 42px;
  padding: 10px 16px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  line-height: 1.2;
  text-transform: uppercase;
  white-space: nowrap;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease, border-color 0.15s ease;
}

.pricing-subscription-btn__content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.pricing-subscription-btn__content i {
  font-size: 0.85rem;
}

.pricing-subscription-btn--primary {
  background: var(--clipr-purple-gradient);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 8px 28px var(--clipr-purple-glow);
}

.pricing-subscription-btn--primary:hover:not(:disabled),
.pricing-subscription-btn--primary:focus:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 36px rgba(168, 85, 247, 0.38);
}

.pricing-subscription-btn--ghost {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.14);
  color: var(--clipr-text-primary);
}

.pricing-subscription-btn--ghost:hover:not(:disabled),
.pricing-subscription-btn--ghost:focus:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.22);
  color: #fff;
}

.pricing-subscription-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
}

.pricing-plan-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0 0 20px;
  padding: 12px 14px;
  border: 1px solid rgba(196, 181, 253, 0.2);
  border-radius: 12px;
  background: rgba(15, 23, 42, 0.38);
  color: var(--clipr-text-secondary);
  font-size: 0.8rem;
  font-weight: 600;
}

.pricing-plan-actions__buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.pricing-plan-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 34px;
  padding: 7px 10px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  color: var(--clipr-text-primary);
  font-size: 0.7rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.pricing-plan-action-btn:hover:not(:disabled),
.pricing-plan-action-btn:focus-visible {
  border-color: rgba(196, 181, 253, 0.55);
  background: rgba(168, 85, 247, 0.13);
  outline: none;
}

.pricing-plan-action-btn--primary {
  border-color: rgba(167, 139, 250, 0.5);
  color: #ede9fe;
}

.pricing-plan-action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pricing-cancellation-modal {
  width: min(100%, 460px);
}

.pricing-cancellation-modal__intro {
  margin-bottom: 20px;
}

.pricing-cancellation-modal__field {
  display: block;
  margin-bottom: 16px;
}

.pricing-cancellation-modal__field > span {
  display: block;
  margin-bottom: 7px;
  color: var(--clipr-text-primary);
  font-size: 0.8rem;
  font-weight: 700;
}

.pricing-cancellation-modal__field select,
.pricing-cancellation-modal__field textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--clipr-border-color);
  border-radius: 9px;
  outline: none;
  background: var(--clipr-bg-elevated);
  color: var(--clipr-text-primary);
  font: inherit;
}

.pricing-cancellation-modal__field textarea {
  resize: vertical;
}

.pricing-cancellation-modal__field select:focus,
.pricing-cancellation-modal__field textarea:focus {
  border-color: var(--clipr-border-purple);
  box-shadow: 0 0 0 3px rgba(168, 85, 247, 0.16);
}

.pricing-cancellation-modal__error {
  margin: -4px 0 0;
  color: #fca5a5;
  font-size: 0.8rem;
}

.confirm-modal-btn--danger {
  border-color: rgba(248, 113, 113, 0.5);
  background: rgba(239, 68, 68, 0.17);
  color: #fee2e2;
}

.confirm-modal-btn--danger:hover:not(:disabled),
.confirm-modal-btn--danger:focus-visible {
  border-color: rgba(252, 165, 165, 0.85);
  background: rgba(239, 68, 68, 0.28);
  outline: none;
}

.pricing-subscription-divider {
  margin: 0 8px;
  color: var(--clipr-text-tertiary);
}

.pricing-subscription-alert {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-color: rgba(248, 113, 113, 0.45);
  background: rgba(248, 113, 113, 0.12);
}

.pricing-subscription-alert-title {
  margin: 0;
  color: #fecaca;
  font-weight: 600;
}

.pricing-subscription-alert-text {
  color: #fee2e2;
  font-size: 0.9rem;
  line-height: 1.45;
}

.pricing-subscription-alert-action {
  flex: 0 0 auto;
  min-height: 38px;
  padding: 9px 16px;
  border: 1px solid rgba(254, 202, 202, 0.45);
  border-radius: 999px;
  background: rgba(254, 202, 202, 0.12);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 1.2;
  text-transform: uppercase;
  transition: transform 0.15s ease, background 0.15s ease, border-color 0.15s ease;
}

.pricing-subscription-alert-action:hover:not(:disabled),
.pricing-subscription-alert-action:focus:not(:disabled) {
  transform: translateY(-1px);
  background: rgba(254, 202, 202, 0.2);
  border-color: rgba(254, 202, 202, 0.7);
}

.pricing-subscription-alert-action:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.pricing-cancellation-card {
  border-color: rgba(134, 239, 172, 0.28);
  background: linear-gradient(145deg, rgba(134, 239, 172, 0.08), rgba(15, 23, 42, 0.45));
  border-radius: 16px;
  padding: 0;
  overflow: hidden;
}

.pricing-cancellation-card-inner {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 22px;
}

.pricing-cancellation-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.pricing-cancellation-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  background: rgba(134, 239, 172, 0.15);
  color: #86efac;
  flex-shrink: 0;
}

.pricing-cancellation-title {
  font-weight: 600;
  font-size: 1rem;
  color: #bbf7d0;
}

.pricing-cancellation-summary {
  margin-top: 4px;
  font-size: 0.875rem;
  color: var(--clipr-text-secondary);
}

.pricing-cancellation-date {
  padding: 10px 14px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 0.875rem;
  color: var(--clipr-text-primary);
}

.pricing-cancellation-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.pricing-cancellation-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 100px;
  padding: 10px 14px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.pricing-cancellation-stat-label {
  font-size: 0.75rem;
  color: var(--clipr-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.pricing-cancellation-stat-value {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--clipr-text-primary);
}

.pricing-selection-notice-text {
  color: var(--clipr-text-primary);
  font-size: 0.9rem;
  line-height: 1.45;
}

.billing-cycle-switch-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 4px;
}

.pricing-flow-shell {
  overflow-x: hidden;
  overflow-y: visible;
}

.pricing-flow-track {
  display: flex;
  width: 200%;
  align-items: flex-start;
  transition: transform 0.4s ease;
  will-change: transform;
}

.pricing-flow-shell--checkout .pricing-flow-track {
  transform: translateX(-50%);
}

.pricing-flow-pane {
  width: 50%;
  flex: 0 0 50%;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: var(--pricing-section-gap);
}

.pricing-flow-pane--catalog {
  padding-right: 12px;
}

.pricing-flow-pane--checkout {
  padding-left: 0;
}

.billing-cycle-switch {
  display: inline-flex;
  padding: 4px;
  border-radius: 999px;
  border: 1px solid var(--clipr-border-color);
  background: rgba(0, 0, 0, 0.25);
  gap: 4px;
}

.billing-cycle-option {
  padding: 10px 22px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--clipr-text-secondary);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
}

.billing-cycle-option-active {
  background: #f1f5f9;
  color: #0f172a;
}

.billing-cycle-option-active:hover {
  color: #0f172a;
}

.billing-cycle-option:hover:not(.billing-cycle-option-active) {
  color: var(--clipr-text-primary);
}

.pending-purchase {
  margin-top: 16px;
  border: 1px solid var(--clipr-border-color);
  border-radius: var(--clipr-radius-md);
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.02);
}

.pending-purchase--confirmed {
  border-color: rgba(74, 222, 128, 0.45);
  background: rgba(74, 222, 128, 0.12);
}

.pending-purchase--failed {
  border-color: rgba(248, 113, 113, 0.45);
  background: rgba(248, 113, 113, 0.12);
}

.pending-purchase-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.pricing-section-divider {
  width: 100%;
  margin: 0;
  border: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.pricing-section-stack {
  display: flex;
  flex-direction: column;
  gap: var(--pricing-section-gap);
}

.pricing-catalog-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  margin: 0 auto 26px;
  padding-top: 24px;
  max-width: 760px;
  text-align: center;
}

.pricing-catalog-hero__title {
  margin: 0;
  padding-bottom: 0.08em;
  font-size: clamp(2rem, 4.6vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.2;
  background: linear-gradient(120deg, #f8fafc 0%, #ddd6fe 46%, #67e8f9 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.pricing-catalog-hero__subtitle {
  margin: 0;
  max-width: 56ch;
  color: var(--clipr-text-secondary);
  font-size: clamp(0.98rem, 1.6vw, 1.12rem);
  font-weight: 500;
  line-height: 1.55;
}

.section-meta {
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--clipr-border-color);
  color: var(--clipr-text-secondary);
  font-size: 0.82rem;
  white-space: nowrap;
}

.plans-grid {
  --plan-card-height: 430px;

  display: grid;
  /* Catalog now holds exactly two tiers: Free | Unlimited. */
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: 1fr;
  align-items: stretch;
  gap: 16px;
  width: min(100%, 880px);
  margin: 0 auto;
}

.plans-grid--four {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.plans-grid--four .plan-card-inner {
  gap: 8px;
  padding: 14px 16px 16px;
}

.plans-grid--four .plan-name {
  font-size: 1.05rem;
}

.plans-grid--four .plan-price {
  font-size: 1.7rem;
}

.plans-grid--four .plan-price-original {
  font-size: 0.95rem;
}

.plans-grid--four .plan-billing {
  font-size: 0.62rem;
}

.plans-grid--four .plan-highlights-item {
  font-size: 0.8rem;
}

.plans-grid--four .plan-card-cta {
  padding: 9px 12px;
  font-size: 0.84rem;
}

.plan-card {
  position: relative;
  display: flex;
  flex-direction: column;
  height: auto;
  min-height: var(--plan-card-height);
  border-radius: 22px;
  padding: 1px;
  background: linear-gradient(
    145deg,
    rgba(196, 181, 253, 0.28) 0%,
    rgba(148, 163, 184, 0.1) 45%,
    rgba(34, 211, 238, 0.18) 100%
  );
  box-shadow:
    0 18px 40px rgba(0, 0, 0, 0.28),
    0 0 0 1px rgba(255, 255, 255, 0.03);
  color: var(--clipr-text-primary);
  text-align: left;
  transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.28s ease;
  overflow: visible;
}

.plan-card::before {
  display: none;
}

.plan-card-glow {
  position: absolute;
  inset: -40% -20% auto;
  height: 70%;
  background: radial-gradient(ellipse at center, rgba(168, 85, 247, 0.22), transparent 68%);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.28s ease;
}

/* Free tier: quieter treatment so Unlimited visually wins without making Free illegible.
   Declared before .plan-card-current so the "current plan" green accent still takes over. */
.plan-card--free {
  background: linear-gradient(
    145deg,
    rgba(148, 163, 184, 0.22) 0%,
    rgba(148, 163, 184, 0.08) 50%,
    rgba(148, 163, 184, 0.14) 100%
  );
  box-shadow:
    0 14px 32px rgba(0, 0, 0, 0.22),
    0 0 0 1px rgba(255, 255, 255, 0.02);
}

.plan-card--free .plan-card-glow {
  display: none;
}

.plan-card--free .plan-card-inner {
  background:
    radial-gradient(ellipse 90% 55% at 50% -10%, rgba(148, 163, 184, 0.08), transparent 58%),
    rgba(9, 9, 14, 0.94);
}

.plan-card--free .plan-price {
  background: none;
  -webkit-background-clip: initial;
  background-clip: initial;
  color: #e2e8f0;
}

.plan-card-inner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  min-height: 100%;
  border-radius: 21px;
  padding: 14px 16px 16px;
  background:
    radial-gradient(ellipse 90% 55% at 50% -10%, rgba(168, 85, 247, 0.14), transparent 58%),
    radial-gradient(ellipse 60% 40% at 100% 100%, rgba(34, 211, 238, 0.06), transparent 62%),
    rgba(9, 9, 14, 0.94);
  backdrop-filter: blur(12px);
}

.plan-card-clickable {
  cursor: pointer;
}

.plan-card-clickable:hover {
  transform: translateY(-4px);
  box-shadow:
    0 26px 52px rgba(0, 0, 0, 0.34),
    0 0 40px rgba(124, 58, 237, 0.14);
}

.plan-card-clickable:hover .plan-card-glow {
  opacity: 1;
}

.plan-card-main {
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-height: 0;
}

.plan-card-buy {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-height: 0;
}

.plan-name {
  align-self: flex-start;
  margin: 0;
  color: #f8fafc;
  font-size: 1.7rem;
  font-weight: 900;
  line-height: 1.25;
  letter-spacing: -0.02em;
  text-align: left;
}

.plan-card-ribbon {
  position: absolute;
  top: -13px;
  right: 14px;
  z-index: 3;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 13px;
  border-radius: 999px;
  border: 1px solid rgba(34, 211, 238, 0.4);
  background: rgba(34, 211, 238, 0.12);
  color: #67e8f9;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  line-height: 1.2;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.26);
}

.plan-card-ribbon i {
  font-size: 0.72rem;
}

.plan-card-ribbon--featured {
  border-color: #fde047;
  background: #facc15;
  color: #422006;
}

.plan-card-ribbon--current {
  background: rgba(74, 222, 128, 0.14);
  border: 1px solid rgba(74, 222, 128, 0.28);
  color: #dcfce7;
}

.plan-card-featured .plan-card-ribbon--current {
  background: rgba(74, 222, 128, 0.14);
  border: 1px solid rgba(74, 222, 128, 0.28);
  color: #dcfce7;
}

.plan-price-row {
  display: flex;
  align-items: baseline;
  gap: 6px 10px;
}

.plan-price-mainline {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: baseline;
  gap: 8px 10px;
  min-width: 0;
}

.plan-discount-row {
  margin: -4px 0 0;
  min-height: 19px;
}

.plan-discount-row--empty {
  visibility: hidden;
}

.plan-price-original {
  display: inline-flex;
  align-items: baseline;
  flex-wrap: nowrap;
  white-space: nowrap;
  gap: 0.2em;
  color: #94a3b8;
  font-size: 1rem;
  font-weight: 700;
  text-decoration: line-through;
  text-decoration-color: rgba(248, 113, 113, 0.82);
  text-decoration-thickness: 2px;
}

.plan-price-discount-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.2;
  color: #bbf7d0;
  background: rgba(34, 197, 94, 0.22);
  border: 1px solid rgba(74, 222, 128, 0.35);
}

.plan-price-discount-value {
  font-weight: 700;
}

.plan-price-discount-word {
  font-weight: 600;
  text-transform: lowercase;
}

.plan-discount-row--stack {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin: -2px 0 0;
  min-height: 0;
}

.plan-price-discount-pill--launch {
  gap: 5px;
  color: #bbf7d0;
  background: rgba(34, 197, 94, 0.22);
  border-color: rgba(74, 222, 128, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.plan-price-discount-pill--launch i {
  font-size: 0.68rem;
  color: #86efac;
}

.checkout-order-amount-column {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  text-align: right;
}

.checkout-order-amount-prices {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: flex-end;
  gap: 8px 10px;
}

.checkout-order-amount-original {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--clipr-text-tertiary);
  text-decoration: line-through;
}

.checkout-order-discount-pill {
  flex-shrink: 0;
}

.plan-price {
  display: inline-flex;
  align-items: baseline;
  flex-wrap: nowrap;
  white-space: nowrap;
  gap: 5px;
  font-size: 1.85rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1;
  background: linear-gradient(120deg, #f5f3ff 0%, #e9d5ff 40%, #67e8f9 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.plan-price-suffix {
  font-size: 0.58rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
  color: #64748b;
  white-space: nowrap;
}

.plans-grid .plan-price.plan-price--discounted {
  background: linear-gradient(120deg, #dcfce7 0%, #86efac 45%, #67e8f9 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.plan-billing {
  color: inherit;
  font-size: 1em;
}

.plan-price-caption {
  margin: 0;
  color: #94a3b8;
  font-size: 0.82rem;
  line-height: 1.4;
}

.plan-token-line {
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid rgba(196, 181, 253, 0.22);
  background: rgba(168, 85, 247, 0.1);
  color: #ddd6fe;
  font-size: 0.78rem;
  font-weight: 600;
}

.plan-token-line i {
  color: #c4b5fd;
  font-size: 0.72rem;
}

.plan-token-equivalency {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.35;
  color: #64748b;
}

.plan-current-note {
  margin: 0;
  padding: 8px 10px;
  border-radius: var(--clipr-radius-md);
  border: 1px solid rgba(74, 222, 128, 0.24);
  background: rgba(74, 222, 128, 0.1);
  color: #dcfce7;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.45;
  overflow: hidden;
}

.plan-highlights-list {
  list-style: none;
  margin: 0;
  padding: 8px 0 0;
  border-top: 1px solid rgba(148, 163, 184, 0.14);
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 0;
  overflow: hidden;
}

.plan-highlights-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #e2e8f0;
  font-size: 0.8rem;
  font-weight: 500;
  line-height: 1.35;
}

.plan-feature-coming-soon {
  flex-shrink: 0;
  margin-top: 1px;
  padding: 2px 6px;
  border-radius: 999px;
  background: rgba(251, 191, 36, 0.16);
  border: 1px solid rgba(251, 191, 36, 0.32);
  color: #fde68a;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 1.2;
  text-transform: uppercase;
}

.plan-benefit-check {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.35) 0%, rgba(6, 182, 212, 0.25) 100%);
  border: 1px solid rgba(196, 181, 253, 0.35);
  box-shadow: 0 0 10px rgba(168, 85, 247, 0.2);
}

.plan-benefit-check i {
  color: #f5f3ff;
  font-size: 0.58rem;
  font-weight: 700;
}

.plan-highlights-item--excluded {
  color: var(--clipr-text-tertiary);
}

.plan-benefit-check--excluded {
  background: rgba(148, 163, 184, 0.16);
  box-shadow: none;
}

.plan-benefit-check--excluded i {
  color: var(--clipr-text-tertiary);
}

.plan-unlocked-benefits {
  padding-top: 8px;
  border-top: 1px solid rgba(148, 163, 184, 0.14);
  min-height: 0;
  overflow: hidden;
}

.plan-unlocked-benefits-title {
  margin: 0 0 6px;
  color: #e9d5ff;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.plan-unlocked-benefits-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.plan-unlocked-benefit-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: var(--clipr-text-secondary, #cbd5e1);
  font-size: 0.8rem;
  line-height: 1.4;
}

.plan-unlocked-benefit-item strong {
  color: #f1f5f9;
  font-weight: 700;
}

.plan-card-featured {
  background: linear-gradient(
    145deg,
    rgba(251, 191, 36, 0.45) 0%,
    rgba(168, 85, 247, 0.42) 38%,
    rgba(34, 211, 238, 0.32) 100%
  );
  box-shadow:
    0 22px 48px rgba(124, 58, 237, 0.28),
    0 0 60px rgba(168, 85, 247, 0.12);
  z-index: 1;
}

.plan-card-featured .plan-card-glow {
  opacity: 0.7;
  background: radial-gradient(ellipse at center, rgba(251, 191, 36, 0.18), transparent 68%);
}

.plan-card-featured:hover {
  transform: translateY(-4px);
  box-shadow:
    0 30px 60px rgba(124, 58, 237, 0.36),
    0 0 70px rgba(168, 85, 247, 0.18);
}

.plan-card-featured .plan-name {
  background: linear-gradient(120deg, #fef3c7 0%, #e9d5ff 55%, #67e8f9 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.plan-card-featured .plan-billing,
.plan-card-featured .plan-price-caption,
.plan-card-featured .plan-token-line,
.plan-card-featured .plan-token-equivalency {
  color: var(--clipr-text-secondary);
}

.plan-card-featured .plan-highlights-item {
  color: #e2e8f0;
}

.plan-card-active:not(.plan-card-featured) {
  box-shadow:
    0 0 0 1px rgba(168, 85, 247, 0.45),
    0 16px 36px rgba(0, 0, 0, 0.3);
}

.plan-card-active.plan-card-featured {
  box-shadow:
    0 0 0 1px rgba(168, 85, 247, 0.48),
    0 20px 42px rgba(0, 0, 0, 0.34);
}

.plan-card-current {
  background: linear-gradient(
    145deg,
    rgba(74, 222, 128, 0.4) 0%,
    rgba(168, 85, 247, 0.28) 42%,
    rgba(34, 211, 238, 0.22) 100%
  );
  box-shadow:
    0 0 0 1px rgba(74, 222, 128, 0.22),
    0 18px 38px rgba(0, 0, 0, 0.3);
}

.plan-card-current.plan-card-active,
.plan-card-current.plan-card-featured,
.plan-card-current.plan-card-active.plan-card-featured {
  box-shadow:
    0 0 0 1px rgba(74, 222, 128, 0.24),
    0 20px 42px rgba(0, 0, 0, 0.34);
}

.plan-card-actions {
  margin-top: auto;
  padding-top: 6px;
}

.plan-card-cta {
  position: relative;
  overflow: hidden;
  width: 100%;
  min-height: 40px;
  padding: 9px 12px;
  border-radius: 14px !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  background: linear-gradient(135deg, #c084fc 0%, #7c3aed 48%, #0891b2 100%) !important;
  box-shadow:
    0 12px 28px rgba(124, 58, 237, 0.32),
    inset 0 1px 0 rgba(255, 255, 255, 0.18) !important;
  font-weight: 800;
  font-size: 0.82rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  transition: transform 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease;
}

.plan-card-featured .plan-card-cta {
  background: linear-gradient(135deg, #fde68a 0%, #a855f7 42%, #06b6d4 100%) !important;
  box-shadow:
    0 14px 32px rgba(168, 85, 247, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.22) !important;
}

.plan-card-cta:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow:
    0 18px 36px rgba(124, 58, 237, 0.42),
    inset 0 1px 0 rgba(255, 255, 255, 0.22) !important;
  filter: brightness(1.06);
}

.plan-card-cta:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none !important;
}

.plan-card-cta--current,
.plan-card-cta--current:hover,
.plan-card-cta--current:focus {
  background: linear-gradient(135deg, #a855f7 0%, #6366f1 48%, #06b6d4 100%) !important;
  border-color: rgba(196, 181, 253, 0.5) !important;
  color: #fff !important;
  box-shadow:
    0 14px 32px rgba(124, 58, 237, 0.38),
    inset 0 1px 0 rgba(255, 255, 255, 0.22) !important;
}

.plan-card-cta--current:disabled {
  opacity: 1 !important;
  cursor: default !important;
}

.pricing-checkout-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.checkout-back {
  align-self: flex-start;
  border-color: rgba(147, 51, 234, 0.5) !important;
  color: rgba(192, 132, 252, 0.9) !important;
}

.checkout-back:hover,
.checkout-back:focus {
  border-color: rgba(147, 51, 234, 0.7) !important;
  color: var(--clipr-purple-light) !important;
  background: rgba(147, 51, 234, 0.1) !important;
}

.checkout-brand-badge {
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #f5f3ff;
  font-size: 0.82rem;
  font-weight: 900;
  letter-spacing: 0.02em;
  text-shadow: 0 0 16px rgba(196, 181, 253, 0.36);
}

.checkout-brand-mark {
  width: 24px;
  height: 24px;
  object-fit: contain;
  filter: drop-shadow(0 0 12px rgba(168, 85, 247, 0.62));
}

.checkout-shell {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 0.92fr);
  gap: 18px;
  align-items: start;
}

.checkout-order-card,
.checkout-payment-card {
  position: relative;
  border-radius: 22px;
  padding: 1px;
  background: linear-gradient(
    135deg,
    rgba(196, 181, 253, 0.36) 0%,
    rgba(148, 163, 184, 0.12) 54%,
    rgba(34, 211, 238, 0.22) 100%
  );
  box-shadow:
    0 18px 40px rgba(0, 0, 0, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  overflow: hidden;
}

.checkout-payment-card {
  grid-column: 1;
  grid-row: 1;
}

.checkout-order-card {
  grid-column: 2;
  grid-row: 1;
  position: sticky;
  top: 72px;
}

.checkout-order-card-glow,
.checkout-payment-card-glow {
  position: absolute;
  inset: 0;
  border-radius: 22px;
  pointer-events: none;
  background:
    radial-gradient(ellipse 80% 50% at 20% 0%, rgba(168, 85, 247, 0.2), transparent 60%),
    radial-gradient(ellipse 60% 45% at 100% 100%, rgba(34, 211, 238, 0.1), transparent 65%);
}

.checkout-order-card-inner,
.checkout-payment-card-inner {
  position: relative;
  z-index: 1;
  min-height: 100%;
  border-radius: 21px;
  padding: 22px 20px;
  background:
    radial-gradient(ellipse 70% 50% at 0% 0%, rgba(168, 85, 247, 0.1), transparent 58%),
    radial-gradient(ellipse 55% 45% at 100% 100%, rgba(34, 211, 238, 0.06), transparent 62%),
    rgba(9, 9, 14, 0.96);
  backdrop-filter: blur(14px);
}

.checkout-panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 18px;
  color: #f8fafc;
  font-size: 0.92rem;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.checkout-panel-title i {
  color: #c4b5fd;
  font-size: 0.95rem;
}

.checkout-order-hero {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 16px;
  border: 1px solid rgba(196, 181, 253, 0.2);
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.12) 0%, rgba(6, 182, 212, 0.06) 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.checkout-plan-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid rgba(251, 191, 36, 0.35);
  background: rgba(251, 191, 36, 0.1);
  color: #fde68a;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.02em;
}

.checkout-plan-badge i {
  color: #fbbf24;
  font-size: 0.78rem;
}

.checkout-price-display {
  display: flex;
  align-items: baseline;
  gap: 10px;
  min-width: 0;
  white-space: nowrap;
}

.checkout-price-display--discounted {
  gap: 8px;
  flex-wrap: nowrap;
}

.checkout-price-original {
  flex: 0 0 auto;
  color: #94a3b8;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1;
  text-decoration: line-through;
  text-decoration-color: rgba(248, 113, 113, 0.82);
  text-decoration-thickness: 2px;
}

.checkout-price-amount {
  font-size: 2.4rem;
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1;
  background: linear-gradient(120deg, #faf5ff 0%, #e9d5ff 45%, #67e8f9 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.checkout-price-display--discounted .checkout-price-amount {
  font-size: 2.05rem;
}

.checkout-price-currency {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.checkout-order-meta {
  margin: 14px 0 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid rgba(196, 181, 253, 0.22);
  background: rgba(168, 85, 247, 0.1);
  color: #ddd6fe;
  font-size: 0.8rem;
  font-weight: 600;
}

.checkout-order-meta i {
  color: #c4b5fd;
  font-size: 0.74rem;
}

.checkout-shell .form-control,
.checkout-shell .country-search-input {
  min-height: 46px;
  border-radius: 14px;
  border: 1px solid rgba(196, 181, 253, 0.24);
  background: rgba(15, 15, 20, 0.88);
  color: var(--clipr-text-primary, #f8fafc);
  padding: 11px 14px;
  font-size: 0.88rem;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 2px 8px rgba(0, 0, 0, 0.12);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.checkout-shell .form-control:focus,
.checkout-shell .country-search-input:focus {
  outline: none;
  border-color: rgba(196, 181, 253, 0.72);
  background: rgba(15, 15, 20, 0.95);
  color: var(--clipr-text-primary, #f8fafc);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 0 0 3px rgba(168, 85, 247, 0.16),
    0 4px 16px rgba(124, 58, 237, 0.12);
}

.checkout-shell .form-control::placeholder,
.checkout-shell .country-search-input::placeholder {
  color: var(--clipr-text-tertiary, #64748b);
}

.checkout-field {
  min-width: 0;
}

.checkout-exp-input {
  width: 100%;
}

.checkout-form-divider {
  margin: 8px 0 14px;
  border: 0;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(196, 181, 253, 0.28) 50%,
    transparent 100%
  );
}

.checkout-field-label--invalid {
  color: #fca5a5 !important;
}

.checkout-field-error,
.checkout-form-error {
  margin: 8px 0 0;
  color: #fca5a5;
  font-size: 0.82rem;
  line-height: 1.45;
  font-weight: 500;
}

.checkout-form-error {
  margin-top: 12px;
}

.checkout-promo-card {
  position: relative;
  margin-top: 16px;
  border-radius: 16px;
  padding: 1px;
  background: linear-gradient(
    135deg,
    rgba(196, 181, 253, 0.35) 0%,
    rgba(148, 163, 184, 0.12) 55%,
    rgba(34, 211, 238, 0.28) 100%
  );
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  overflow: hidden;
}

.checkout-promo-card-glow {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(ellipse 70% 80% at 0% 0%, rgba(168, 85, 247, 0.16), transparent 62%),
    radial-gradient(ellipse 55% 70% at 100% 100%, rgba(34, 211, 238, 0.1), transparent 65%);
}

.checkout-promo-card-body {
  position: relative;
  z-index: 1;
  padding: 14px;
  border-radius: 15px;
  background:
    linear-gradient(160deg, rgba(168, 85, 247, 0.08) 0%, rgba(9, 9, 14, 0.92) 42%),
    rgba(12, 12, 18, 0.96);
}

.checkout-promo-card--valid {
  background: linear-gradient(
    135deg,
    rgba(74, 222, 128, 0.42) 0%,
    rgba(34, 197, 94, 0.22) 50%,
    rgba(34, 211, 238, 0.28) 100%
  );
  box-shadow:
    0 8px 28px rgba(34, 197, 94, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.checkout-promo-card--valid .checkout-promo-card-glow {
  background:
    radial-gradient(ellipse 80% 70% at 20% 0%, rgba(74, 222, 128, 0.18), transparent 62%),
    radial-gradient(ellipse 60% 55% at 100% 100%, rgba(34, 211, 238, 0.08), transparent 65%);
}

.checkout-promo-card--invalid {
  background: linear-gradient(
    135deg,
    rgba(248, 113, 113, 0.35) 0%,
    rgba(196, 181, 253, 0.18) 55%,
    rgba(34, 211, 238, 0.16) 100%
  );
}

.checkout-promo-title {
  margin: 0 0 10px;
  color: #e2e8f0;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.35;
}

.checkout-promo-row {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.checkout-promo-input-wrap {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
}

.checkout-promo-input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  font-size: 0.82rem;
  pointer-events: none;
  transition: color 0.2s ease;
}

.checkout-promo-input-wrap:focus-within .checkout-promo-input-icon {
  color: #c4b5fd;
}

.checkout-promo-input {
  width: 100%;
  min-height: 42px;
  padding-left: 34px !important;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  font-size: 0.82rem !important;
}

.checkout-promo-btn {
  flex: 0 0 auto;
  min-height: 42px;
  padding: 0 16px !important;
  border-radius: 12px !important;
  border: 1px solid rgba(196, 181, 253, 0.45) !important;
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.28) 0%, rgba(6, 182, 212, 0.18) 100%) !important;
  color: #f5f3ff !important;
  font-weight: 800;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow:
    0 6px 18px rgba(124, 58, 237, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
}

.checkout-promo-btn:not(:disabled):hover {
  transform: translateY(-1px);
  filter: brightness(1.08);
  box-shadow:
    0 10px 22px rgba(124, 58, 237, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.checkout-promo-btn:disabled {
  opacity: 0.5;
}

.checkout-promo-btn-icon {
  display: inline-block;
  margin-right: 4px;
}

.checkout-promo-applied {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: nowrap;
}

.checkout-promo-code-chip {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  flex: 1 1 auto;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px dashed rgba(74, 222, 128, 0.45);
  background: rgba(74, 222, 128, 0.1);
  color: #bbf7d0;
  font-size: 0.82rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.checkout-promo-remove {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: transparent;
  color: #94a3b8;
  font-size: 0.76rem;
  font-weight: 700;
  padding: 6px 8px;
  border-radius: 8px;
  transition: color 0.2s ease, background 0.2s ease;
}

.checkout-promo-remove:not(:disabled):hover {
  color: #fca5a5;
  background: rgba(248, 113, 113, 0.08);
}

.checkout-promo-remove i {
  font-size: 0.68rem;
}

.checkout-promo-message {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 10px 0 0;
  padding: 9px 11px;
  border-radius: 10px;
  font-size: 0.78rem;
  line-height: 1.45;
}

.checkout-promo-message--compact {
  align-items: center;
  width: fit-content;
  margin: 8px 0 0;
  padding: 5px 9px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 800;
  line-height: 1;
}

.checkout-promo-message--ok {
  color: #bbf7d0;
  border: 1px solid rgba(74, 222, 128, 0.22);
  background: rgba(74, 222, 128, 0.08);
}

.checkout-promo-message--error {
  color: #fecaca;
  border: 1px solid rgba(248, 113, 113, 0.22);
  background: rgba(248, 113, 113, 0.08);
}

.checkout-promo-message i {
  flex-shrink: 0;
  margin-top: 2px;
  font-size: 0.86rem;
}

.checkout-promo-message--compact i {
  margin-top: 0;
  font-size: 0.72rem;
}

.checkout-pro-benefits {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid rgba(148, 163, 184, 0.16);
}

.checkout-pro-benefits-title {
  margin: 0 0 12px;
  color: #e9d5ff;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.checkout-pro-benefits-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.checkout-pro-benefit-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--clipr-text-secondary, #cbd5e1);
  font-size: 0.82rem;
  line-height: 1.4;
}

.checkout-pro-benefit-item strong {
  color: #f1f5f9;
  font-weight: 700;
}

.checkout-benefit-check {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.4) 0%, rgba(6, 182, 212, 0.3) 100%);
  border: 1px solid rgba(196, 181, 253, 0.4);
  box-shadow: 0 0 12px rgba(168, 85, 247, 0.22);
}

.checkout-benefit-check i {
  color: #faf5ff;
  font-size: 0.62rem;
  font-weight: 700;
}

.card-number-field {
  position: relative;
}

.card-number-field .form-control {
  padding-right: 56px;
}

.card-brand-indicator {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  line-height: 1;
  pointer-events: none;
}

.card-brand-indicator--amex {
  color: #5ec7f2;
}

.card-brand-indicator--visa {
  color: #5b8df7;
}

.card-brand-indicator--mastercard {
  color: #ff6b4a;
}

.checkout-pay-cta {
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid rgba(148, 163, 184, 0.14);
}

.checkout-pay-btn {
  position: relative;
  overflow: hidden;
  min-height: 50px;
  border-radius: 14px !important;
  background: linear-gradient(135deg, #c084fc 0%, #7c3aed 45%, #0891b2 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.14) !important;
  box-shadow:
    0 14px 32px rgba(124, 58, 237, 0.38),
    inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
  font-weight: 800;
  font-size: 0.88rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  transition: transform 0.22s ease, box-shadow 0.22s ease, filter 0.22s ease;
}

.checkout-pay-btn-shine {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    105deg,
    transparent 40%,
    rgba(255, 255, 255, 0.18) 50%,
    transparent 60%
  );
  transform: translateX(-120%);
  animation: checkout-pay-shine 3.5s ease-in-out infinite;
  pointer-events: none;
}

@keyframes checkout-pay-shine {
  0%,
  70%,
  100% {
    transform: translateX(-120%);
  }
  85% {
    transform: translateX(120%);
  }
}

.checkout-pay-btn:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow:
    0 20px 40px rgba(124, 58, 237, 0.48),
    inset 0 1px 0 rgba(255, 255, 255, 0.24) !important;
  filter: brightness(1.05);
}

.checkout-pay-btn:disabled {
  opacity: 0.55;
}

.checkout-legal {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  font-size: 0.74rem;
  color: #64748b !important;
  line-height: 1.45;
  text-align: center;
}

.checkout-legal i {
  color: #86efac;
  font-size: 0.78rem;
}

.country-search {
  position: relative;
  display: flow-root;
}

.country-search-input--selected {
  caret-color: transparent;
  cursor: pointer;
  padding-right: 54px;
}

.country-search-clear {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: 0;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  color: var(--clipr-text-secondary);
}

.country-search-clear i {
  display: block;
  font-size: 0.72rem;
  line-height: 1;
}

.country-search-clear:hover,
.country-search-clear:focus {
  background: rgba(168, 85, 247, 0.16);
  color: var(--clipr-text-primary);
  outline: none;
}

.country-search-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  background: var(--clipr-bg-elevated);
  border: 1px solid var(--clipr-border-color);
  border-radius: var(--clipr-radius-md);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.35);
  z-index: 20;
}

.country-search-option {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: 0;
  background: transparent;
  color: var(--clipr-text-primary);
  text-align: left;
}

.country-search-option:hover,
.country-search-option:focus {
  background: rgba(168, 85, 247, 0.12);
  outline: none;
}

.country-search-code {
  color: var(--clipr-text-secondary);
  font-size: 0.8rem;
  letter-spacing: 0.04em;
}

.pricing-success-overlay {
  overflow: hidden;
}

.pricing-success-modal {
  position: relative;
  z-index: 1;
}

.pricing-success-title {
  font-size: 1.65rem;
}

.pricing-success-body {
  position: relative;
  z-index: 1;
  padding-top: 8px;
  padding-bottom: 28px;
  text-align: center;
}

.pricing-success-copy {
  max-width: 360px;
  margin: 0 auto;
  font-size: 0.98rem;
  line-height: 1.6;
  white-space: pre-line;
}

.pricing-request-message {
  color: var(--clipr-text-secondary);
  line-height: 1.6;
  white-space: pre-line;
}

.billing-celebration {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
}

.billing-celebration-piece {
  position: absolute;
  top: -12vh;
  left: var(--billing-confetti-left);
  width: 10px;
  height: 18px;
  border-radius: 999px;
  background: var(--billing-confetti-color);
  opacity: 0;
  transform: translate3d(0, -12vh, 0) rotate(0deg);
  animation: billing-confetti-fall var(--billing-confetti-duration) linear infinite;
  animation-delay: var(--billing-confetti-phase);
  will-change: transform, opacity;
}

.ti-spin {
  display: inline-block;
  animation: billing-spin 1s linear infinite;
}

.pricing-page .btn i {
  margin-right: 5px;
}

@keyframes billing-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes billing-confetti-fall {
  0% {
    opacity: 0;
    transform: translate3d(0, -12vh, 0) rotate(0deg);
  }

  10% {
    opacity: 1;
  }

  82% {
    opacity: 1;
  }

  100% {
    opacity: 0;
    transform: translate3d(var(--billing-confetti-drift), 105vh, 0) rotate(var(--billing-confetti-rotate));
  }
}

@media (max-width: 991px) {
  .pricing-status-grid {
    grid-template-columns: 1fr;
  }

  .plans-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }

  .plan-card-inner {
    gap: 8px;
    padding: 14px;
  }

  .plan-name {
    font-size: 1.02rem;
  }

  .plan-card-ribbon {
    padding: 3px 8px;
    font-size: 0.66rem;
  }

  .plan-price {
    font-size: 1.65rem;
  }

  .plan-price-original {
    font-size: 0.95rem;
  }

  .plan-price-caption,
  .plan-token-line,
  .plan-current-note {
    font-size: 0.75rem;
  }

  .plan-highlights-list {
    gap: 8px;
  }

  .plan-highlights-item {
    gap: 8px;
    font-size: 0.8rem;
  }

  .plan-card-actions {
    padding-top: 6px;
  }

  .plan-card-cta {
    padding: 9px 12px;
    font-size: 0.82rem;
  }

  .pricing-flow-track {
    display: block;
    width: 100%;
    transform: none !important;
    transition: none;
    will-change: auto;
  }

  .pricing-flow-pane {
    width: 100%;
    flex: none;
  }

  .pricing-flow-shell--checkout .pricing-flow-pane--catalog {
    display: none;
  }

  .pricing-flow-shell:not(.pricing-flow-shell--checkout) .pricing-flow-pane--checkout {
    display: none;
  }

  .checkout-shell {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .checkout-payment-card,
  .checkout-order-card {
    grid-column: auto;
    grid-row: auto;
  }

  .checkout-order-card {
    position: relative;
    top: auto;
  }

  .checkout-payment-form {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 14px;
    margin-left: 0;
    margin-right: 0;
  }

  .checkout-payment-form > .checkout-field {
    width: auto;
    max-width: none;
    flex: initial;
    margin-bottom: 0 !important;
    padding-left: 0;
    padding-right: 0;
  }

  .checkout-field--full {
    grid-column: 1 / -1;
  }

  .checkout-field--compact {
    grid-column: span 1;
  }
}

@media (max-width: 768px) {
  .pricing-page {
    --pricing-mobile-inset: var(--clipr-layout-mobile-content-inset);
    --pricing-mobile-title-rhythm: var(--clipr-layout-mobile-title-rhythm);
    --pricing-section-gap: var(--clipr-layout-mobile-flow-rhythm);
    padding: 0 0 1.25rem;
  }

  .pricing-inner {
    padding-left: var(--pricing-mobile-inset);
    padding-right: var(--pricing-mobile-inset);
  }

  .pricing-page--embedded .pricing-inner {
    padding-left: var(--pricing-embedded-horizontal-inset);
    padding-right: var(--pricing-embedded-horizontal-inset);
    padding-bottom: var(--pricing-embedded-vertical-inset);
  }
}

@media (max-width: 767px) {
  .pricing-checkout-section {
    gap: 12px;
  }

  .checkout-back {
    padding: 8px 12px;
    font-size: 0.82rem;
  }

  .checkout-panel-title {
    margin-bottom: 14px;
    font-size: 1rem;
  }

  .checkout-order-row {
    flex-direction: column;
    gap: 10px;
  }

  .checkout-order-label {
    font-size: 0.9rem;
  }

  .checkout-order-amount-column {
    align-items: flex-start;
    gap: 6px;
    text-align: left;
  }

  .checkout-order-amount-prices {
    justify-content: flex-start;
    gap: 6px 8px;
  }

  .checkout-order-amount {
    font-size: 1.18rem;
  }

  .checkout-order-amount-original {
    font-size: 0.88rem;
  }

  .checkout-pay-btn {
    padding: 13px 16px;
    font-size: 0.95rem;
  }

  .checkout-legal {
    margin-top: 12px;
    font-size: 0.78rem;
    line-height: 1.5;
  }

  .pricing-balance-row,
  .pricing-subscription-row,
  .pricing-subscription-alert,
  .pending-purchase-main {
    flex-direction: column;
    align-items: flex-start;
  }

  .pricing-subscription-row {
    align-items: flex-start;
  }

  .pricing-subscription-actions {
    width: 100%;
    max-width: none;
    margin-left: 0;
    justify-content: flex-start;
  }

  .pricing-subscription-alert-action {
    width: 100%;
  }

  .plans-grid {
    grid-template-columns: 1fr;
  }

  .pricing-flow-pane--catalog,
  .pricing-flow-pane--checkout {
    padding-left: 0;
    padding-right: 0;
  }
}

@media (max-width: 575px) {
  .pricing-page {
    padding-left: var(--clipr-layout-mobile-page-gutter-sm);
    padding-right: var(--clipr-layout-mobile-page-gutter-sm);
  }

  .checkout-payment-card,
  .checkout-order-card {
    border-radius: 18px;
  }

  .checkout-payment-card-inner,
  .checkout-order-card-inner {
    border-radius: 17px;
    padding: 18px 14px;
  }

  .checkout-payment-form {
    gap: 10px 12px;
  }

  .checkout-shell .form-control {
    font-size: 0.95rem;
  }
}
</style>
